// Persistence layer for tenant state — debounced save, background refresh,
// conflict detection, and the base updateState() primitive.
//
// Concurrency invariant: at most one state-write call may be in flight at a
// time.  The debounce timer and flushPendingSave both check inFlightSaveRef
// before starting a new write.  After a 409 (a concurrent writer won), the
// handler compares which top-level keys changed externally against which keys
// the user changed:
//   • No overlap → safe merge: user's keys are applied on top of fresh server
//     state and a silent retry is queued. Covers sweeper races and users in
//     different modules — the banner never fires.
//   • Overlap → genuine human conflict: fresh state is adopted and the banner
//     is shown so the user can re-apply their changes. DB-CONC-001 applies.
// A 401 stops retries (the session is gone) and warns the user; transient
// network errors are re-queued and retried.
//
// The same invariant extends to writers OUTSIDE this file. documents-write and
// documents-workflow also write tenant_state (a document-number claim, an AI
// training capture, a status transition), so they bump the row version too, and
// the action that triggers them dirties local state in the same tick — arming
// the 1.5s debounce. They hand the new version back through adoptStateVersion,
// but only once they return, and a save that takes longer than the debounce
// loses that race: the timer fires with the superseded version and the server
// refuses it. To the classifier below that refusal looks exactly like a second
// human editing the tenant, so a user working alone is told their workspace
// changed elsewhere and their work was not saved (GH #5314 — both refusals
// observed in production fired ~1.5s after a document action, one debounce
// exactly). beginExternalStateWrite closes that window: those callers claim the
// version for the duration of their write and the debounce waits them out.
//
// The second way a lone user is told they have company: a write that failed
// with no answer at all — a dropped connection, a timeout, a 5xx. A refusal
// (401/409) proves nothing was stored, but silence proves nothing either way:
// the request may have reached the server and committed with only the response
// lost. The cached version is then stale, every retry is refused, and this
// file's own resolution finds the row holding values it did not put there and
// calls that another session (GH #5191). unverifiedWriteRef remembers what the
// unanswered write was carrying so the classifier can tell our own late-landing
// write apart from a real one. It is evidence about the row only until the next
// acknowledged write, which is why it is cleared there.
//
// Silent-loss invariant (GH #5037): the conflict banner must be reachable from
// EVERY way the resolution can end with the user's own change not surviving —
// not just the fully-successful overlap path at the end of the chain. A 409
// whose resolution is abandoned part-way (no usable reload snapshot, an
// unclassifiable baseline, a deferral that never settles) used to return early
// and dispatch nothing at all, so the edit was dropped while the user was still
// looking at a UI that showed it saved. The rule below is "tell the user when
// their own change did not survive" — deliberately NOT "tell the user whenever
// a 409 occurred", because a non-overlapping 409 is a normal, silently-recovered
// event and bannering it would be pure noise.
//
// Save receipts (GH #5037 / #5170): the conflict banner above is an app-shell
// backstop, and it is deliberately silent whenever a 409 merges cleanly. That
// leaves the surface the user was actually looking at — the modal that just
// closed, the "✓ Saved" chip on a settings tab — with no way to learn what
// happened to the write it triggered, so it reported success unconditionally
// and was structurally incapable of doing otherwise. updateState() therefore
// returns an edit generation, and every terminal outcome of the write carrying
// that generation announces itself on 'seasuite:state-saved' /
// 'seasuite:state-save-failed' with { generation, reason }. src/state/
// useSaveStatus.js is the consumer; nothing else in the persistence machinery
// depends on a receipt being observed, so an unobserved edit behaves exactly as
// it did before.
//
// Receipts are keyed off the SNAPSHOT OBJECT the write actually carried (a
// WeakMap populated inside updateState's own setState updater), not off the
// free-running localGenerationRef counter. Those differ: the counter is bumped
// synchronously at updateState() call time, while the snapshot only exists once
// React runs the updater. Reporting the counter would claim a generation the
// payload does not contain whenever a debounce timer fires inside that window —
// which is precisely the "told the user it saved when it did not" failure this
// exists to remove.
//
// Race guard (GH #3594): inFlightSaveRef is cleared as soon as the failed
// write's .catch handler returns — NOT once the 409 handler's own
// reload-and-merge finishes, since that reload runs detached (fire-and-forget)
// rather than being returned/awaited by the catch handler. So a newer local
// edit can land, debounce, and even successfully save WHILE an earlier 409's
// reload is still in flight. If that reload then unconditionally applied its
// setState(merged)/setState(fresh) — built from the stateToSave snapshot
// captured before the newer edit existed — it would silently clobber it, with
// no error or banner. localGenerationRef is a counter bumped by every
// updateState() call; persistState captures it at call start and the 409
// handler re-checks it immediately before applying its snapshot, skipping
// (and deferring to the newer edit's own save cycle) when it has moved on —
// the same pattern #3423 added to the background refresh() path below.
import { useState, useEffect, useCallback, useRef } from 'react';
import { INITIAL_STATE } from './initialState.js';
import {
  loadTenantStateWithVersion,
  saveTenantStateVersioned,
  saveTenantStatePatch,
} from '../api/tenantApi.js';
import { diffState, applyPatch } from '../../azure-functions/shared/state/statePatch.cjs';
import { stripGapReports } from '../../azure-functions/shared/gap/reportRows.cjs';
import {
  rebaseOnConflict,
  describeConflict,
  mergeEmployeeEdits,
  keepMineOp,
  applyKeepMine,
  collapseGone,
} from './patchConflicts.js';
import { migrateState } from '../constants/scfMigration.js';
import { logDiag } from '../diagnostics/diagnosticsClient.js';
import { reportConflictBanner } from './conflictReport.js';
import { registerTenantStateVersionHooks } from '../api/tenantStateVersion.js';
import { createRealtimeClient } from '../api/realtimeClient.js';
import { emitRealtimeSignal, setRealtimeStatus } from './realtimeSignals.js';
import {
  hasRecordLevelOverlap,
  mergeCollectionKey,
  mergeWithEngineCarveOut,
} from './collectionRegistry.js';
import { saveEmployeeRecords } from '../api/employeeApi.js';
import { dispatchWriteErrorBanner, clearPendingWriteErrorNotice } from '../api/writeErrors.js';
import { describeRewritten } from './rewrittenMessages.js';
import { diffEmployeesForWrite } from './employeeWriteDiff.js';
import { hasPendingRiskWrites } from './riskWriteOutbox.js';
import { hasPendingRecordWrites } from './pendingRecordWrites.js';
import {
  TASK_TYPE as GAP_TASK_TYPE,
  SERVER_OWNED_FIELDS as GAP_TASK_SERVER_OWNED_FIELDS,
} from '../../azure-functions/shared/employees/competenceGapTask.cjs';
import { PLAN_GATE_MARKER_KEY } from '../utils/planGateMarker.js';
import {
  writeTeardownBackup,
  readTeardownBackup,
  clearTeardownBackup,
  isTeardownBackupStale,
  purgeLegacyTeardownBackups,
} from './teardownBackup.js';

// Risk keys are authoritative in tenant_risk_state and owned by the per-record
// write outbox (ADR-0001). They must never travel in the blob save: the server
// already strips them for migrated tenants, and shipping them wastes payload
// and re-bloats the column this migration exists to shrink. Stripping them
// client-side also lets a risk-only edit skip the blob write entirely.
const RISK_BLOB_KEYS = ['risks', 'riskAssertions', 'riskDecisions', 'riskObligations', 'evidence'];

// Control ownership / effectiveness / SoA data are authoritative in
// dbo.tenant_control_entities and owned by the controls-write per-record API
// (ADR-0004, CSCB-01). Same rationale as RISK_BLOB_KEYS above — state-write
// mirrors the strip-if-migrated / restore-if-absent contract for these keys.
const CONTROL_BLOB_KEYS = ['controlOwnership', 'controlEffectiveness', 'soaData'];

// Asset / BIA data are authoritative in dbo.tenant_asset_entities and owned
// by the assets-write per-record API (ADR-0005, ASBC-03/04). Same rationale
// as RISK_BLOB_KEYS/CONTROL_BLOB_KEYS above — state-write mirrors the
// strip-if-migrated / restore-if-absent contract for these keys, and its
// restore branch lands in the same PR as this strip, never after (ADR-0005
// §5.5 risk #5 — the client strip must never reach production ahead of the
// server-side restore for an un-migrated tenant).
const ASSET_BLOB_KEYS = ['assets', 'biaAssessments'];

// BC/DR plans/runbooks/runs/backups/DR tests/pen tests are authoritative in
// dbo.tenant_bcdr_entities and owned by the bcdr-write per-record API
// (ADR-0006, BCA-03/04). Same rationale as RISK_BLOB_KEYS/CONTROL_BLOB_KEYS
// above — state-write mirrors the strip-if-migrated / restore-if-absent
// contract for these six keys.
const BCDR_BLOB_KEYS = [
  'bcdrPlans',
  'bcdrRunbooks',
  'bcdrRunbookRuns',
  'bcdrBackups',
  'bcdrDrTests',
  'bcdrPenTests',
];

// Confirmed incidents / IR exercises are authoritative in
// dbo.tenant_incident_entities and owned by the incidents-write per-record
// API (ADR-0007, INA-03/04). Same rationale as RISK_BLOB_KEYS/BCDR_BLOB_KEYS
// above — state-write mirrors the strip-if-migrated / restore-if-absent
// contract for these two keys, and its restore branch lands in the same PR
// as this strip, never after (ADR-0007 §3's backward-compatibility section —
// the client strip must never reach production ahead of the server-side
// restore for an un-migrated tenant). Deliberately does NOT include
// `incidentProposals` — that key is a read-time-only overlay never
// persisted in the blob (INC-B-002).
const INCIDENT_BLOB_KEYS = ['incidents', 'incidentExercises'];

// The standalone evidence/artefact register is authoritative in
// dbo.tenant_artefacts and owned by the artefacts-write per-record API
// (ADR-0009, ARTA-03/04). Same rationale as RISK_BLOB_KEYS/ASSET_BLOB_KEYS
// above — state-write mirrors the strip-if-migrated / restore-if-absent
// contract for this key. `artefactSettings` is deliberately NOT included:
// it stays in the blob (ADR-0009 §2.1), and stripping it would reset a
// tenant's configured verificationIntervalDays back to the 90-day default,
// silently changing when artefacts are flagged unverified via Gate 4.
const ARTEFACT_BLOB_KEYS = ['artefacts'];

// Employees are authoritative in dbo.tenant_employees and owned by the
// employees-write per-record API (ADR-0008, EMPA-03/04). Same rationale as
// RISK_BLOB_KEYS/CONTROL_BLOB_KEYS/ASSET_BLOB_KEYS/BCDR_BLOB_KEYS above —
// state-write mirrors the strip-if-migrated / restore-if-absent contract for
// this key. The per-record write (diffEmployeesForWrite, above) already runs
// ahead of the blob write regardless of this strip; this is what stops the
// same records ALSO travelling — redundantly, and increasingly stalely once
// employees-write stops writing its own blob copy — inside the blob payload.
const EMPLOYEE_BLOB_KEYS = ['employees'];

// GH #6012 (and its #5845/#6149/#6158 siblings): documents are already
// authoritative in dbo.tenant_documents — every save round-trips through
// saveDocumentPatch (documents-write), never the blob — yet this client kept
// sending the full `state.documents` array in every blob save anyway,
// including the beforeunload/pagehide keepalive teardown write below. A
// document's array entry carries its whole version history plus up to three
// content snapshots (useDocumentState.js's MAX_VERSION_HISTORY/
// KEEP_SNAPSHOTS), so editing and saving a document is one of the largest
// single contributors to the teardown write exceeding its 64 KiB keepalive
// budget (azureClient.js's KEEPALIVE_BODY_BUDGET_BYTES) — the failure mode
// GH #6158's teardown-backup comment above already names this issue for.
// Stripping `documents` here removes that contribution from every save,
// teardown included, and the server side of this has already shipped:
// state-write's `needsDocuments` slice-restore (shared/state/assemble.js's
// "Documents migration (phase 2)" comment) exists specifically to backfill
// this key from the stored row once the client stops sending it — it has
// simply never had a client that omits the key to serve, until now.
//
// Unlike the groups above, `documents` is NOT a "strip-if-migrated" key:
// state-write's own comment ("Documents migration (phase 2)... We
// deliberately do NOT strip state.documents from this write") explains that
// an un-migrated tenant can still hold a document ONLY in the legacy
// state.documents[] array, so the endpoint continues to ACCEPT and persist
// the key if a caller sends it rather than refusing it. This strip is
// therefore one-directional — stop sending a key nobody needs from us — not
// the two-sided declare-and-refuse contract BLOB_KEYS_OWNED_BY_STORES below
// gives the other twelve families. `documents` is deliberately left out of
// that declaration list and out of NOT_PERSISTED_FAMILY_KEYS: state-write
// never refuses it, so it can never appear in a `notPersisted` response.
// Phase 3 (removing the legacy fallback entirely, tenant by tenant) is a
// separate, larger piece of work than this issue.
const DOCUMENT_BLOB_KEYS = ['documents'];

// ── GH #6148, the last four ────────────────────────────────────────────────
//
// These four families were the ones this client kept sending after their store
// became authoritative — not because a blob save could still store them (for a
// migrated tenant state-write has stripped them for as long as the store has
// existed) but because their keys live in the same in-memory state object the
// rest of the app saves, and nobody removed them on the way out.
//
// Every write path for all four goes through its own per-record endpoint
// (findings-write, ai-governance-write, supplier-*-crud, vendors-write), each
// with its own optimistic rollback and its own error surface; the local state
// mutation that follows is an echo of the stored record, never the write
// itself. So stripping them here loses nothing a migrated tenant had, and it
// makes state-write's contract literally true: nothing this client sends is
// dropped, so nothing can be dropped behind a 200.
//
// The server's restore-if-absent half for all four lands in the SAME PR as
// this strip, never after (ADR-0005 §5.5 risk #5) — an un-migrated tenant
// still holds these keys in the blob, and a save that simply omits them would
// otherwise wipe the module before its store had ever been seeded.
const FINDINGS_BLOB_KEYS = ['findings'];

const AI_BLOB_KEYS = [
  'aiSystems',
  'aiRiskProfileOverrides',
  'aiProhibitedScreenings',
  'tias',
  'aiScores',
  'aiSettings',
];

const SUPPLIER_BLOB_KEYS = [
  'customers',
  'deliveryAgreements',
  'engagements',
  'subcontractorInvolvements',
  'supplierEvidence',
  'supplierRiskConfig',
  'securityPackages',
  'agreements',
  'agreementCommitments',
];

const VENDOR_BLOB_KEYS = ['vendors', 'vendorAssessmentConfig'];

// A read-time overlay state-read serves and state-write has always stripped
// unconditionally — it has never lived in the blob for any tenant, migrated or
// not, so there is nothing to restore and no un-migrated case to protect. It is
// here because a key we echo back is a key state-write reports as dropped, and
// the contract above admits no exceptions.
const FINDING_PROPOSALS_BLOB_KEYS = ['findingProposals'];

// #6608 — the plan-gate marker. Not a per-record-store key like every family
// above: it is not tenant data at all, it is state-read's statement of which
// keys IT withheld from this response (src/utils/planGateMarker.js). It must
// never enter the [state] column, because a marker persisted into the blob is
// then re-served by assemble.js to whoever reads next — including a tenant who
// has since upgraded — and would turn their compliance evaluators not-entitled
// on data they are fully entitled to. state-write deletes it from every
// incoming payload as well; this strip is the half that keeps it out of the
// request in the first place.
const PLAN_GATE_MARKER_BLOB_KEYS = [PLAN_GATE_MARKER_KEY];

// CDEA-01 (GH #6952) — the retired server-side document draft mirror. The
// editor no longer reads or writes it and state-read no longer serves it;
// state-write deletes it from every incoming payload too (so an old,
// not-yet-reloaded bundle cannot put it back). This strip is the client half:
// the key never reaches the request, even from a state object that still
// holds a copy from before the deploy.
const RETIRED_BLOB_KEYS = ['documentDrafts'];

// PDWB-03 — the workflow evidence overlay (azure-functions/shared/state/
// assemble.js). Derived from the workflow tables on every read and never
// stored: state-write strips it unconditionally (like the proposal overlays),
// and this is the client half, so a save never carries a stale copy of run
// evidence back into the request.
const WORKFLOW_EVIDENCE_BLOB_KEYS = ['workflowEvidence'];

// Returns a copy of `state` with the given keys removed, or the same reference
// when it carries none of them (so callers can cheaply detect "these keys were
// present").
function stripKeys(state, keys) {
  if (!state || typeof state !== 'object') return state;
  let present = false;
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(state, k)) {
      present = true;
      break;
    }
  }
  if (!present) return state;
  const out = { ...state };
  for (const k of keys) delete out[k];
  return out;
}

// The families above, in one list, so a new one is added in a single place
// rather than threaded through a nest of wrapper calls.
const BLOB_OWNED_KEY_GROUPS = [
  RISK_BLOB_KEYS,
  CONTROL_BLOB_KEYS,
  ASSET_BLOB_KEYS,
  BCDR_BLOB_KEYS,
  INCIDENT_BLOB_KEYS,
  ARTEFACT_BLOB_KEYS,
  EMPLOYEE_BLOB_KEYS,
  DOCUMENT_BLOB_KEYS,
  FINDINGS_BLOB_KEYS,
  AI_BLOB_KEYS,
  SUPPLIER_BLOB_KEYS,
  VENDOR_BLOB_KEYS,
  FINDING_PROPOSALS_BLOB_KEYS,
  PLAN_GATE_MARKER_BLOB_KEYS,
  RETIRED_BLOB_KEYS,
  WORKFLOW_EVIDENCE_BLOB_KEYS,
];

function stripBlobOwnedKeys(state) {
  let out = state;
  for (const keys of BLOB_OWNED_KEY_GROUPS) out = stripKeys(out, keys);
  // FV4A-07 (ADR-0014): a gap report lives in its own row, written only by
  // gap-reports-write and the report worker. It is never part of a blob save,
  // so adopting a stored report on screen never echoes it back.
  return stripGapReports(out);
}

// GH #6148 — the families stripBlobOwnedKeys above removes, named the way
// state-write names them. Declaring one is a promise never to send it: if a key
// from a declared family reaches state-write anyway, the endpoint REFUSES the
// whole save with 409 BLOB_KEYS_NOT_PERSISTED instead of dropping it behind a
// 200. Keep this list in lock-step with stripBlobOwnedKeys — adding a strip
// without adding its name here just loses the refusal, and adding a name
// without the strip breaks every save for the family.
const BLOB_KEYS_OWNED_BY_STORES = [
  'risk',
  'controls',
  'assets',
  'bcdr',
  'incidents',
  'artefacts',
  'employees',
  'findings',
  'aiGovernance',
  'supplier',
  'vendors',
  'findingProposals',
];

// The key list behind each family name, used to explain a `notPersisted` entry
// this client did not expect.
//
// Every family this bundle knows about is now stripped and declared, so on a
// current build a reported family is a 409, not a 200 with a list. This map is
// what covers the two cases that survive that: an older bundle still in a tab
// during a deploy's rollout window, and a family added server-side that this
// map has not caught up with. In both, an echo of the server's own snapshot is
// harmless — it is what state-read overlaid — but a copy that DIFFERS from the
// last snapshot is a local edit whose per-record write did not land and whose
// blob save is not carrying it either. That raises the write banner; a plain
// echo stays silent.
const NOT_PERSISTED_FAMILY_KEYS = {
  risk: RISK_BLOB_KEYS,
  controls: CONTROL_BLOB_KEYS,
  assets: ASSET_BLOB_KEYS,
  bcdr: BCDR_BLOB_KEYS,
  incidents: INCIDENT_BLOB_KEYS,
  artefacts: ARTEFACT_BLOB_KEYS,
  employees: EMPLOYEE_BLOB_KEYS,
  findings: FINDINGS_BLOB_KEYS,
  aiGovernance: AI_BLOB_KEYS,
  supplier: SUPPLIER_BLOB_KEYS,
  vendors: VENDOR_BLOB_KEYS,
  findingProposals: FINDING_PROPOSALS_BLOB_KEYS,
};

/**
 * Which of the families state-write says it dropped actually carried an edit
 * this client had and the server had not seen.
 *
 * @param {string[]} families  the endpoint's `notPersisted` list
 * @param {object} sent        the state object handed to state-write
 * @param {object|null} lastServerState  the last snapshot the server gave us
 * @returns {string[]} families whose content diverged — i.e. a real lost write
 */
function divergedNotPersistedFamilies(families, sent, lastServerState) {
  if (!Array.isArray(families) || !families.length) return [];
  return families.filter((family) => {
    const keys = NOT_PERSISTED_FAMILY_KEYS[family];
    // An unknown family is reported rather than assumed harmless: a new strip
    // added server-side must not go unnoticed here just because this map is
    // behind. Fail towards telling the user.
    if (!keys) return true;
    return keys.some(
      (k) =>
        Object.prototype.hasOwnProperty.call(sent || {}, k) &&
        !sameJson(sent[k], lastServerState?.[k])
    );
  });
}

function approxBytes(v) {
  try {
    return JSON.stringify(v).length;
  } catch {
    return -1;
  }
}

// Returns the top-level state keys whose values differ between two snapshots.
// Uses JSON serialisation for deep equality since updateState always deep-copies.
// Conservative: serialisation failures are treated as changed.
function getChangedTopLevelKeys(before, after) {
  if (!before || !after) return [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changed = [];
  for (const k of keys) {
    try {
      if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) changed.push(k);
    } catch {
      changed.push(k);
    }
  }
  return changed;
}

// Deep equality for two state values, on the same JSON basis (and with the
// same conservative failure handling) as getChangedTopLevelKeys above.
/** RCQA-04: the task with this id in state.adhocTasks, if any. */
function findTask(s, id) {
  return Array.isArray(s?.adhocTasks) ? s.adhocTasks.find((t) => t && t.id === id) : undefined;
}

/** RCQA-04: replace each task by id in state.adhocTasks, appending new ones. */
function upsertTasks(s, tasks) {
  const list = Array.isArray(s?.adhocTasks) ? s.adhocTasks : [];
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const next = list.map((t) => (t && byId.has(t.id) ? byId.get(t.id) : t));
  const present = new Set(list.map((t) => t && t.id));
  for (const t of tasks) if (!present.has(t.id)) next.push(t);
  return { ...s, adhocTasks: next };
}

function sameJson(a, b) {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

// Fire a cross-cutting app event (same pattern as 'seasuite:session-expired').
// Guarded so it is a no-op outside a browser (SSR / tests).
function dispatchAppEvent(name, detail) {
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(
        detail === undefined ? new CustomEvent(name) : new CustomEvent(name, { detail })
      );
    }
  } catch {
    /* non-browser environment — no-op */
  }
}

const SAVE_DEBOUNCE_MS = 1500;
// Ceiling on how long an edit may sit undurable while the debounce keeps being
// re-armed. Every updateState() restarts the 1.5s timer, so a user who keeps
// typing — a ROPA description, an incident narrative, a plan's scope field —
// never stops restarting it, and nothing is written for as long as they keep
// going. Measured: fifty keystrokes 200ms apart produced not one request in ten
// seconds; close the tab there and every word is gone (GH #5358). The debounce
// exists to coalesce keystrokes, not to postpone durability indefinitely, so
// past this point the oldest pending edit is written and the debounce starts
// coalescing again from there.
const MAX_SAVE_DEFER_MS = 5_000;
const REFRESH_INTERVAL_MS = 60_000;
const REFRESH_THROTTLE_MS = 5_000;

// GH #6393 — a failed background refresh used to be a console line and nothing
// else. The user carried on editing against a snapshot the app already knew it
// had failed to refresh, which on a shared tenant is exactly the window in
// which two people's edits diverge and one is silently lost.
//
// The overwhelmingly common cause is a cold Function App answering 503 for a
// few seconds after a deploy, which fixes itself — so a banner on the FIRST
// failure would fire routinely and be learned-away, leaving us worse off than
// silence. Retry quietly with backoff instead, and only tell the user once the
// retries are exhausted, i.e. once "stale" has stopped being transient.
const REFRESH_RETRY_BACKOFF_MS = [2_000, 6_000, 15_000];

// Deadline for ONE attempt of the boot state-read. This read is the only thing
// standing between a signed-in user and the app shell (App.jsx renders
// <SplashScreen /> for as long as `stateReady` is false), and the non-env read
// path carries no request timeout of its own — storage-azure.js says so in
// readTenantStateStrictWithVersion's own signature note: "env calls carry their
// own request timeout". So a state-read that HANGS rather than fails never
// reaches the retry loop's second attempt, never sets stateLoadError, and
// leaves the app on the splash screen indefinitely with nothing on screen and
// no way for the user to act. #4915 built the retry screen for exactly that
// outcome; it only ever covered a read that answers with an error.
//
// 30s matches ENV_DEFAULT_TIMEOUT_MS in azureClient.js, which is the deadline
// the env-scoped read path already applies to the same endpoint. Generous
// enough to ride out a Function App cold start, bounded enough that three
// attempts still land on the retry screen rather than never.
const STATE_LOAD_ATTEMPT_TIMEOUT_MS = 30_000;

// Deadline for ONE save attempt. Same gap as the read above, in the direction
// that costs data rather than availability: nothing ever gave up on an
// in-flight blob write. inFlightSaveRef is cleared only in persistState's
// .finally(), so a state-write that HANGS rather than fails leaves it true for
// the rest of the session — every later edit then hits the in-flight guard,
// marks itself dirty, and waits for a settle that never comes. The user is
// told nothing either: the "changes may not have been saved" banner is driven
// by writeFailuresRef, which only moves when a write actually fails.
//
// Deliberately a deadline on WAITING, not an abort of the request (see
// withWriteDeadline). A write that is merely slow may still be committing, and
// cancelling it would throw away work the server was about to store. Letting it
// run while this attempt gives up is exactly the "unanswered write may have
// landed" case unverifiedWriteRef already exists for (GH #5191): the timeout
// lands in the retryable branch, which keeps the edit, records what the
// unanswered write was carrying, raises the banner, and retries on a backoff.
//
// 45s rather than the read's 30s: a whole-blob write under contention is
// legitimately slower than a read, and a premature give-up costs a retry plus
// a 409 resolution. Still bounded, which is the whole point.
const WRITE_ATTEMPT_TIMEOUT_MS = 45_000;

// GH #6158/#6149/#6012: a teardown write (beforeunload/pagehide) has no
// reliable delivery mechanism once the page starts unloading — a keepalive
// fetch is capped at 64 KiB and every real tenant's whole-state blob exceeds
// that (azureClient.js's KEEPALIVE_BODY_BUDGET_BYTES comment), and the plain
// fetch it falls back to has no delivery guarantee across a navigation either
// (net::ERR_ABORTED). Both failure modes are silent: no banner, no retry,
// the edit is simply gone. localStorage.setItem is synchronous and commits
// before the handler returns, so it survives teardown even when the network
// request does not — the write is backed up here first, then replayed on the
// next boot if it never got confirmed.
// The key shape, the staleness bound and the legacy sweep live in
// ./teardownBackup.js — useAuth's logout() clears the same key (GH #6307), so
// the two callers must not each own their own copy of the key format.

/**
 * Resolve/reject with `promise`, or reject with a TimeoutError once the
 * deadline passes — whichever happens first. The underlying request is left
 * running on purpose (see WRITE_ATTEMPT_TIMEOUT_MS); Promise.race simply stops
 * this attempt from waiting on it forever.
 *
 * TimeoutError is what writeErrors.isNetworkFailure already classifies as a
 * transport failure, so the outcome routes through the existing retry contract
 * rather than needing one of its own.
 */
function withWriteDeadline(promise) {
  // Once this attempt has given up, the request it stopped waiting on is still
  // running and may still reject. Marking it handled here keeps that from
  // surfacing as an unhandled rejection; Promise.race attaches its own
  // handlers independently, so this does not swallow the outcome below.
  promise.catch(() => {});
  let timer = null;
  const deadline = new Promise((_resolve, reject) => {
    timer = setTimeout(() => {
      const err = new Error(
        `state-write did not answer within ${WRITE_ATTEMPT_TIMEOUT_MS}ms — treating this attempt as unanswered`
      );
      err.name = 'TimeoutError';
      reject(err);
    }, WRITE_ATTEMPT_TIMEOUT_MS);
  });
  return Promise.race([promise, deadline]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

// Retry backoff for a save that failed for a retryable reason (offline, 5xx,
// throttle). The retry keeps going for as long as the failure lasts — an
// offline window is not bounded by any particular number of attempts — but the
// interval grows so a long outage does not spin a 1.5s hot loop for minutes.
const RETRY_BACKOFF_MS = [1_500, 3_000, 6_000, 12_000, 30_000];
// Consecutive failures before the app-shell "changes may not have been saved"
// banner is raised. The first failure is usually a blip the next attempt
// clears, and warning on it would cry wolf constantly; by the second the user
// genuinely needs to know their edit has not landed yet.
const WRITE_ERROR_BANNER_AFTER_FAILURES = 2;

// How often connectivity is re-checked directly while a save is pending or
// retrying. #5658 fixed this same gap in networkRetryQueue.js: the 'online'
// event is "not delivered at all by the network-emulation layers used to
// test this," so a reconnect the event misses leaves only the backoff ladder,
// and a save made offline can still be sitting unretried when the user
// navigates away — which is how a queued ROPA (GDPR Article 30) entry was
// never written and never reported (TRYGGR_INTENT.md §5, GH #5763).
// navigator.onLine is always readable, so polling it makes the reliable half
// of the signal the primary one instead of a backstop for the event alone.
const ONLINE_POLL_MS = 1_000;

// Consecutive 409 resolutions abandoned because a newer local edit raced the
// reload (see the generation-race guard below). One deferral on its own is not
// worth a banner: the racing edit's own save cycle re-conflicts and resolves
// normally a moment later, and warning on it would fire on the perfectly safe
// merge GH #3594 covers. But nothing guarantees the next cycle settles either —
// while it keeps deferring, the version ref is never adopted, every save 409s,
// and the user is told nothing at all. Past this many consecutive deferrals we
// stop assuming the next cycle will resolve it and say so.
const CONFLICT_DEFERRAL_BANNER_AFTER = 3;

// The same ceiling, denominated in wall-clock time instead of cycles.
//
// Counting cycles alone budgets the user's silence in ROUND TRIPS, and a round
// trip is not a fixed quantity: GH #5704 measured state-write at a median of
// 2.1s and a p95 of 16.9s on a live tenant. Three sequential refusals is ~6s of
// silence at the median and ~51s at the p95 — so the slower the server gets,
// the longer a tab that has stopped saving keeps saying nothing, which is
// precisely backwards. GH #5661 reports that silence as the mechanism being
// broken; it is not, it is the mechanism's budget being denominated in the one
// unit that degrades with the failure.
//
// Whichever ceiling is reached first ends the run. 10s is clear of three
// median-latency cycles, so a healthy tenant's ordinary recovered race stays
// silent exactly as before and this only fires where cycles alone would have
// left the user uninformed for the better part of a minute.
//
// The run is timed from the moment the FIRST refused write was issued, not from
// the moment its refusal was classified, so a single round trip that has
// already consumed the whole budget on its own does not buy two more.
const CONFLICT_DEFERRAL_BANNER_AFTER_MS = 10_000;

// How often the debounced save re-checks whether a version-bumping write by
// another path (a document save, a workflow action) has finished — see
// beginExternalStateWrite below. Short enough that the deferred save feels
// immediate once the other write lands.
const EXTERNAL_WRITE_RECHECK_MS = 150;
// Ceiling on that deferral. The gate is a correctness optimisation, never a
// gate on durability: if a caller's end() is somehow never reached (an
// exception path that skips its finally, a caller that forgets to release),
// the pending edit must still be written rather than held for the rest of the
// session. Past this we go ahead with the version we have — a 409 is recovered
// by the conflict handler below, a never-issued write is not (STG-B-003).
const MAX_EXTERNAL_WRITE_DEFER_MS = 10_000;

function retryDelayFor(failureCount) {
  const i = Math.min(Math.max(failureCount, 1), RETRY_BACKOFF_MS.length) - 1;
  return RETRY_BACKOFF_MS[i];
}

// GH #6607 — the classification a teardown backup needs when the row has moved
// under it, at module scope so the 409 path can run it a second time against the
// state the server hands back with its refusal, and so it is testable on its own.
//
// `changedKeys`/`baseline` are recorded by the teardown handler. A backup
// written before they existed has neither and cannot be merged: payload-vs-row
// differences are then indistinguishable from "the other writer deleted a record
// we still hold", and merging would resurrect it.
//
// Returns one of:
//   { outcome: 'already-stored' }                        nothing of the user's is missing
//   { outcome: 'unclassifiable', missingKeys }            cannot merge, must not guess
//   { outcome: 'resolved', payload, recoverable, unrecoverableKeys, ourWriteDidNotLand }
export function classifyTeardownBackup(backup, freshBlob) {
  const payload = backup.payload;
  const changedKeys = Array.isArray(backup.changedKeys) ? backup.changedKeys : null;
  const keys = changedKeys || Object.keys(payload);

  // Identical in the row: nothing to recover for that key. When that holds for
  // every key the user touched, our own write is what moved the row — the
  // keepalive landed and only its confirmation was lost with the unloading page.
  const missingKeys = keys.filter((k) => !sameJson(freshBlob?.[k], payload[k]));
  if (!missingKeys.length) return { outcome: 'already-stored' };

  if (!changedKeys) return { outcome: 'unclassifiable', missingKeys };

  // Positive evidence the write did NOT land: the row still holds the pre-edit
  // value for something the user changed. Decides how definitely they can be
  // told their work is gone, rather than hedging or over-claiming uniformly.
  const ourWriteDidNotLand = changedKeys.some(
    (k) => backup.baseline && k in backup.baseline && sameJson(freshBlob?.[k], backup.baseline[k])
  );

  const merged = { ...freshBlob };
  const recoverable = [];
  const unrecoverableKeys = [];
  const removedKeys = [];
  for (const k of changedKeys) {
    if (!(k in payload)) {
      // The user REMOVED this top-level key.
      if (!freshBlob || !(k in freshBlob)) {
        // The row does not hold it either — the removal already stands, whoever
        // made it. Telling the user it "is still present" would be a false
        // statement about their own data.
        continue;
      }
      const removedBaseline = backup.baseline?.[k];
      if (sameJson(freshBlob[k], removedBaseline)) {
        // Nobody else has touched this key since the edit was taken, so the
        // removal is safe to carry out rather than to hand back as homework.
        // doneLooksLike is "the edit is stored", not "the user is told to redo
        // it" — a removal we can apply, we apply.
        //
        // An absent key IS a removal for almost everything: state-write does a
        // whole-column replace. The exceptions are the keys it restores from the
        // existing row before writing — `customThemes`, and for a non-admin
        // caller the ADMIN_ONLY_KEYS set (`sso`, `billing`, `activityLogSettings`,
        // `emailSettings`, `ownershipSettings`). Deleting one of those here is a
        // no-op server-side while this counts it recovered, so do not read
        // `recoverable` as "the server removed it" for them. Latent today —
        // nothing in src/** deletes a top-level state key — and the version-match
        // path has always sent the payload with the same keys absent and said
        // nothing, so this matches it rather than introducing the gap.
        delete merged[k];
        recoverable.push(k);
        continue;
      }
      // Someone else moved this key and we cannot express a removal in a merge
      // onto it. Reported rather than dropped — the version-match path replays
      // the payload wholesale and so honours the removal, and the two paths
      // must not disagree in silence about whether a delete was part of the
      // edit. Tracked apart from a collision because the two owe the user
      // different sentences: "re-apply it" would have to mean "delete it
      // again", which is not what that phrase says.
      removedKeys.push(k);
      continue;
    }
    if (sameJson(freshBlob?.[k], payload[k])) {
      // GH #5191's narrowing, applied per key. The row already holds our value
      // here — our teardown write landed for this key and only its confirmation
      // was lost with the unloading page. Nothing to recover and nothing to
      // report: calling it a collision would tell the user their work was lost
      // when the row holds it, and on a create (the #6601 case, a new RoPA
      // entry) send them to re-enter a record that already exists — an extra
      // Article 30 row in a register an auditor reads.
      //
      // Deliberately per key rather than a whole-backup short circuit. The
      // teardown write IS one all-or-nothing versioned write, so a single
      // matching key is real evidence it landed — but only if nothing else
      // could have produced our exact value for that key, and the engine's
      // derivation pass writes this same blob on every session. Discarding the
      // WHOLE backup on that inference would, when it is wrong, drop the other
      // keys' edits with no write and no banner: the pre-#6607 silent loss
      // through a new door. Skipping the key buys the same thing and risks
      // nothing — every other key is still judged on its own merits.
      continue;
    }
    const baselineValue = backup.baseline?.[k];
    if (sameJson(freshBlob?.[k], baselineValue)) {
      // Nobody else touched this key since the user's edit was taken — their
      // value is the whole truth for it.
      merged[k] = payload[k];
      recoverable.push(k);
      continue;
    }
    // Both sides moved this key. Same quad-state as the 409 handler: false /
    // 'engine-only' merge at record granularity; true / null are a genuine
    // same-record collision the user has to re-apply.
    const overlap = hasRecordLevelOverlap(k, baselineValue, freshBlob?.[k], payload[k]);
    if (overlap === true || overlap === null) {
      unrecoverableKeys.push(k);
      continue;
    }
    const granular =
      overlap === 'engine-only'
        ? mergeWithEngineCarveOut(k, freshBlob?.[k], payload[k], baselineValue)
        : mergeCollectionKey(k, freshBlob?.[k], payload[k], baselineValue);
    if (granular === null) {
      // The live 409 path falls back to the user's whole value here, and can:
      // a person is present and the conflict banner is behind it. This runs at
      // boot with nobody watching, so overwriting a whole key with a merge we
      // could not compute would be silent. Report it instead.
      unrecoverableKeys.push(k);
      continue;
    }
    merged[k] = granular;
    recoverable.push(k);
  }

  // Defensive, and believed unreachable: `missingKeys` was non-empty, so at
  // least one key fails the row-already-holds-ours skip and must land in one of
  // the three lists. Kept because the alternative — falling through with three
  // empty lists — would produce a banner naming nothing at all, and a sentence
  // that names no work is worse than no sentence.
  if (!recoverable.length && !unrecoverableKeys.length && !removedKeys.length) {
    return { outcome: 'already-stored' };
  }

  return {
    outcome: 'resolved',
    payload: merged,
    recoverable,
    unrecoverableKeys,
    removedKeys,
    ourWriteDidNotLand,
  };
}

// A person cannot act on `ropaEntries`. writeErrors.js's own header exists to
// forbid exactly this: what the user is told must name their work, not the shape
// it is stored in (STG-B-003). An unmapped key falls through to its own name, so
// a new key is merely unhelpful rather than invisible.
const STATE_KEY_LABELS = {
  ropaEntries: 'Records of Processing Activities',
  dpias: 'Data Protection Impact Assessments',
  dataSubjectRequests: 'data subject requests',
  criticalityScale: 'the criticality scale',
  adhocTasks: 'tasks',
  gapAnalyses: 'gap analyses',
  policies: 'policies',
  frameworks: 'frameworks',
  enabledObligations: 'obligations',
  agreements: 'agreements',
  products: 'products',
  customers: 'customers',
  trainingCourses: 'training courses',
};

// Bounded: a backup written before GH #6607 carries no record of what the user
// changed, so the caller's list is every key that differs from the row — which
// can be most of the blob. A banner that reads as a wall of key names is not a
// sentence a person can act on either.
const MAX_NAMED_KEYS = 3;

// What the user is owed when part or all of a recovered edit did not survive.
//
// A collision and a removal are different events and must not be described with
// one sentence: nobody else touched a key the user deleted, and "re-apply it"
// there would have to mean "delete it again", which is not what that phrase
// says (STG-B-003 — what the user is told is derived from the outcome).
// Certainty is graded too: "was not saved" is only said where the row proves our
// own write never landed; otherwise it may have landed before the other writer
// moved on top of it, and telling someone to re-enter a record that exists is
// how a register gains a duplicate.
function unrecoveredMessage(verdict, { partial }) {
  const collided = verdict.unrecoverableKeys || [];
  const removed = verdict.removedKeys || [];
  if (!collided.length && !removed.length) return null;
  const lead = partial
    ? 'part of a change from your previous session was not restored'
    : 'a change from your previous session was not restored';
  const parts = [];
  if (collided.length) {
    parts.push(
      verdict.ourWriteDidNotLand
        ? `${describeStateKeys(collided)} was not saved — someone else changed the same records in ` +
            'the meantime, so please re-apply it'
        : `${describeStateKeys(collided)} may not have been saved — someone else changed the same ` +
            'records in the meantime, so check it before re-entering anything'
    );
  }
  if (removed.length) {
    parts.push(
      `something you deleted (${describeStateKeys(removed)}) is still present — it could not be ` +
        'removed automatically because this workspace changed elsewhere, so delete it again if you ' +
        'still want it gone'
    );
  }
  return `${lead}: ${parts.join('; and ')}.`;
}

function describeStateKeys(keys) {
  const labels = (keys || []).map((k) => STATE_KEY_LABELS[k] || k);
  if (!labels.length) return 'a change';
  if (labels.length === 1) return labels[0];
  const shown = labels.slice(0, MAX_NAMED_KEYS);
  const rest = labels.length - shown.length;
  const list = `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}`;
  return rest > 0 ? `${list}, and ${rest} other area${rest === 1 ? '' : 's'}` : list;
}

export function usePersistence(tenantId, session) {
  const [state, setState] = useState(INITIAL_STATE);
  const [stateReady, setStateReady] = useState(false);
  const [stateLoadError, setStateLoadError] = useState(null);
  const saveTimer = useRef(null);
  const pendingState = useRef(null);
  const stateRef = useRef(INITIAL_STATE);
  // GH #6306. Read by the teardown paths below, which run on EVERY page this
  // app renders — including the ones with no user session at all (the employee
  // and candidate portals, the magic-link verify screens, the public vendor
  // self-assessment page). `useAppState` is called unconditionally at the top of
  // App.jsx, well above the `/portal` early return, and hooks cannot be
  // conditional, so those surfaces mount this whole persistence layer whether
  // they want it or not. A ref rather than the `session` value itself: the
  // teardown effects are mounted once and must not be torn down and rebound
  // every time a session object identity changes.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  // GH #6307 — the principal the loaded server state belongs to, and therefore
  // the only principal whose session may replay a teardown backup of it.
  //
  // Sticky rather than derived from `session` at teardown time, and set only by
  // a completed authenticated load: the case the backup exists for includes a
  // session that has GONE (expired mid-edit, or a sibling tab logging out),
  // where `session` is already null but the dirty state in memory is still
  // user A's. Reading the owner off the live session there would either lose
  // the recovery or, worse, key A's edit under whoever is logged in next.
  const backupOwnerRef = useRef(null);
  const serverStateRef = useRef(null);
  // GH #7761: the snapshot a write should carry. pendingState is filled by the
  // state-watching effect, i.e. only after React renders, so between an
  // updateState call and that render it still holds the PREVIOUS snapshot —
  // and a writer endpoint's version may already have been adopted in between.
  // stateRef is assigned synchronously by updateState and by every internal
  // setState, so when it is ahead of the server it is the newest unsaved work
  // and a superset of anything queued (a re-queued failed write included: the
  // screen is never reverted under it). Only consulted once something is
  // queued; whether to write at all is still pendingState's call.
  const latestUnsavedSnapshot = useCallback(() => {
    const latest = stateRef.current;
    if (latest && latest !== serverStateRef.current) return latest;
    return pendingState.current;
  }, []);
  const localDirtyRef = useRef(false);
  const inFlightSaveRef = useRef(false);
  const isRefreshingRef = useRef(false);
  const lastRefreshRef = useRef(0);
  // Tracks when fresh server state was last successfully applied (initial load or
  // background refresh). Used to compute the stale window at conflict time.
  const lastRefreshSuccessRef = useRef(0);
  const stateVersionRef = useRef(null);
  // CONC-01: whether state-write takes field-level patches. Cleared for the
  // session the first time a backend answers that it does not.
  const patchSupportedRef = useRef(true);
  // CONC-01: the same-field clashes currently shown to the user, so a
  // "keep mine" choice can be applied against the state that won.
  const fieldConflictsRef = useRef([]);
  // Bumped by every updateState() call. Lets an in-flight 409 conflict
  // resolution detect that a newer local edit has landed since it captured
  // its stateToSave snapshot (GH #3594) — see the file header race-guard note.
  const localGenerationRef = useRef(0);
  // Consecutive retryable save failures. Drives the retry backoff and decides
  // when the write-error banner is raised; reset to 0 by the next success.
  const writeFailuresRef = useRef(0);
  // True once the banner has been raised for the current run of failures, so
  // recovery can clear it and a later run can raise it again.
  const writeErrorShownRef = useRef(false);
  // Consecutive 409 resolutions deferred to a racing local edit's own save
  // cycle. Reset by any successful write and by any resolution that actually
  // completes — see CONFLICT_DEFERRAL_BANNER_AFTER.
  const conflictDeferralsRef = useRef(0);
  // When the current run of deferrals began, or 0 when no run is open. Bounds
  // that run in wall-clock time as well as in cycles — see
  // CONFLICT_DEFERRAL_BANNER_AFTER_MS.
  const conflictRunStartedAtRef = useRef(0);
  // Number of in-flight writes issued by another path (a document save, a
  // workflow action) that may bump tenant_state's row version. While this is
  // above zero the debounced save must not start: it would send the version
  // captured before that write, and the server would refuse it — a 409 a lone
  // user inflicts on themselves. See beginExternalStateWrite.
  const externalStateWritesRef = useRef(0);
  // The payload of a write that failed without an answer, if any. A refusal
  // (401/409) tells us nothing was stored; a dropped connection, a timeout or a
  // 5xx does not — the row may hold that write already, with only the response
  // lost. Kept so the 409 resolution can recognise our own late-landing write
  // in the row instead of reporting it as another session's (GH #5191).
  // Cleared by the next acknowledged write.
  const unverifiedWriteRef = useRef(null);
  // Set when the browser reports connectivity restored while a write was still
  // in flight. The online handler cannot act on that signal itself — it must
  // not start a second concurrent write — and the in-flight write has already
  // taken pendingState, so without recording it here the reconnect is simply
  // discarded and the doomed write's own failure handler schedules the next
  // attempt out at the backoff ceiling (30s). Consumed by the retry paths
  // below, which collapse the backoff instead (GH #5253).
  const reconnectedWhileInFlightRef = useRef(false);
  // When the oldest edit not yet handed to a write was made, or 0 when nothing
  // is outstanding. Bounds how long the debounce may keep being re-armed —
  // see MAX_SAVE_DEFER_MS.
  const oldestPendingEditAtRef = useRef(0);
  // A terminal commit (useCommitNow, `{ commit: true }`) asked for the write to
  // be issued NOW and flushPendingSave had nothing to issue yet.
  //
  // History: updateState used to assign stateRef.current INSIDE its setState
  // updater, which React runs during the render pass rather than at call time,
  // so a commit issued in the same tick as a preceding update (the second half
  // of a double-click is the everyday case) read a stateRef that did not hold
  // the record yet, found nothing to write, and degraded to the 1.5s debounce;
  // a navigation inside that window aborted the write and the record — an
  // ISO 27001 incident, in GH #5660 — was gone with no banner.
  //
  // Since GH #7761 updateState assigns stateRef at call time, so that case no
  // longer arises. flushPendingSave can still find nothing queued when the
  // state-watching effect has not yet run, so the request is still remembered
  // rather than dropped, and that effect honours it the moment it runs, by
  // firing with no debounce instead of waiting the full window.
  //
  const commitRequestedRef = useRef(false);
  // GH #6522: the OTHER way a terminal commit (useCommitNow) can be defeated
  // by timing — not React not having applied the update yet, but
  // flushPendingSave's own "never start a second concurrent write" guard
  // deferring the write because a DIFFERENT one is already in flight (e.g. an
  // earlier action's own debounced save still resolving — the everyday case
  // in a fast multi-step flow, such as enabling an obligation right after
  // editing another module). persistState's finally() below normally re-arms
  // the ordinary SAVE_DEBOUNCE_MS timer for ANY dirty follow-up once the
  // in-flight write settles — correct for an autosave tick that lost the
  // race, wrong for a commit whose caller explicitly asked for "issue the
  // write now": the record then sits undurable in this browser for up to
  // another 1.5s, during which a navigation can abort it with nothing left to
  // retry it.
  //
  // A SEPARATE ref from commitRequestedRef above, deliberately: that ref is
  // also unconditionally cleared inside persistState's own success handler
  // ("whatever a terminal commit was waiting to have written has now been
  // written") as soon as THIS write lands — which runs before finally() below
  // gets a chance to read it, so a flag set for the NEXT (follow-up) write
  // while THIS write was still in flight would be wiped before it could ever
  // be honoured. This ref has exactly one producer (flushPendingSave's
  // in-flight branch) and one consumer (this same finally() block), so
  // nothing else clears it out from under that hand-off.
  const commitAwaitingInFlightRef = useRef(false);
  // Snapshot object → the edit generation it embodies. Populated by
  // updateState as it creates the snapshot, so an entry exists exactly when
  // the snapshot exists. WeakMap because superseded snapshots must be collectable
  // with the state objects they key. See the save-receipt note in the header.
  const snapshotGenerationsRef = useRef(new WeakMap());

  // GH #6307 — invalidate every backup left under the pre-fix tenant-only key.
  // Deliberately unconditional and unguarded by session or tenant: those keys
  // are cross-user replayable by construction, and the page that can remove
  // them is whichever one loads first. Deleted rather than migrated — the
  // stored value records no owner, so re-keying one would be a guess.
  useEffect(() => {
    purgeLegacyTeardownBackups();
  }, []);

  // Returns the load's promise (resolving once the fresh state is applied, or
  // after the failure is logged — it never rejects), so `await reloadState()`
  // actually waits. It used to return undefined, so every caller that awaited
  // it before navigating raced its own reload (GH #7315).
  const reloadState = useCallback(() => {
    if (!tenantId || !session) return Promise.resolve();
    return loadTenantStateWithVersion(tenantId)
      .then(({ state: loaded, stateVersion }) => {
        const migrated = migrateState(loaded);
        serverStateRef.current = migrated;
        stateRef.current = migrated;
        stateVersionRef.current = stateVersion;
        setState(migrated);
      })
      .catch((e) => {
        console.error('[usePersistence] reloadState failed:', e.message);
      });
  }, [tenantId, session]);

  // GH #6158/#6149/#6012: replay a teardown write that localStorage backed up
  // (see TEARDOWN_BACKUP_KEY_PREFIX above) but was never confirmed.
  //
  // A row still at the version the backup was taken against is replayed as-is.
  // A row that has MOVED used to end here, with the backup deleted, on the
  // reasoning that the mover was either the backup's own write landing or a
  // writer whose change we must not clobber. Both halves were guesses, and the
  // second was the expensive kind: an unrelated writer bumping the row was
  // enough to discard a whole unsaved edit — a GDPR Article 30 RoPA record
  // among them — with no banner and no server ever asked (GH #6607, #6601).
  //
  // `state-write` already implements the answer: it refuses a stale write with
  // a 409 AND hands back the state that won, precisely so a client can merge
  // onto it rather than guess. classifyTeardownBackup above does that merge,
  // per key and at record granularity, and the write goes out at the row's
  // CURRENT version so the server still adjudicates. What cannot be merged is
  // said, never dropped in silence.

  const replayTeardownBackupOnce = useCallback(
    async (id, currentVersion, ownerUserId, freshState, onWriteIssued) => {
      if (!ownerUserId) return;
      const backup = readTeardownBackup(id, ownerUserId);
      if (!backup || !backup.payload) return;
      // GH #6307 — a backup that has aged out is not a recovery path any more.
      // The version guard alone does not bound this: on a quiet tenant "nobody
      // has written since" stays true indefinitely, so without the age check a
      // months-old edit is still replayable.
      if (isTeardownBackupStale(backup)) {
        clearTeardownBackup(id, ownerUserId);
        return;
      }

      const giveUp = (message) => {
        clearTeardownBackup(id, ownerUserId);
        dispatchWriteErrorBanner(message);
      };

      // CONC-01 — a backup taken since patches carries the edit itself as
      // field-level operations. Replaying those needs no guess about who moved
      // the row: the server applies them to whatever is stored now, a change
      // made elsewhere in the meantime cannot refuse them, and a field changed
      // to something else is named and shown with both values.
      if (Array.isArray(backup.ops) && patchSupportedRef.current) {
        if (!backup.ops.length) {
          clearTeardownBackup(id, ownerUserId);
          return;
        }
        onWriteIssued();
        try {
          await withWriteDeadline(saveTenantStatePatch(id, backup.ops));
          clearTeardownBackup(id, ownerUserId);
          console.warn(
            '[usePersistence] Recovered an edit that failed to save when a previous tab/session closed.'
          );
        } catch (e) {
          if (e?.httpStatus === 409 && Array.isArray(e.conflicts) && e.conflicts.length) {
            // The non-clashing part was NOT written either (all-or-nothing);
            // re-send it without the clashing operations, then tell the user
            // about the rest with both values.
            const conflicts = [...e.conflicts];
            let serverState = e.serverState;
            const clashing = new Set(conflicts.map((c) => JSON.stringify(c.path)));
            const rest = backup.ops.filter((op) => !clashing.has(JSON.stringify(op.path)));
            clearTeardownBackup(id, ownerUserId);
            if (rest.length) {
              try {
                await withWriteDeadline(saveTenantStatePatch(id, rest));
              } catch (restError) {
                // The non-clashing part is the user's only copy now. A second
                // clash is named alongside the first; whatever did not clash is
                // kept for the next boot rather than dropped in silence.
                let unsaved = rest;
                if (
                  restError?.httpStatus === 409 &&
                  Array.isArray(restError.conflicts) &&
                  restError.conflicts.length
                ) {
                  conflicts.push(...restError.conflicts);
                  if (restError.serverState != null) serverState = restError.serverState;
                  const again = new Set(restError.conflicts.map((c) => JSON.stringify(c.path)));
                  unsaved = rest.filter((op) => !again.has(JSON.stringify(op.path)));
                }
                if (
                  unsaved.length &&
                  !writeTeardownBackup(id, ownerUserId, { ...backup, ops: unsaved })
                ) {
                  dispatchWriteErrorBanner(
                    'Some changes recovered from a closed tab could not be saved. Check your recent edits.'
                  );
                }
              }
            }
            const fresh = serverState != null ? migrateState(serverState) : freshState || {};
            // The record as the user last saw it lives in the backup's own
            // baseline (the stored copy of every key they changed), so a
            // record deleted since the tab closed can still be offered for a
            // fail-safe restore, not only shown as gone.
            const lastSeen = { ...(freshState || {}), ...(backup.baseline || {}) };
            const notice = collapseGone(conflicts, backup.payload, lastSeen).map((c) =>
              describeConflict(c, fresh)
            );
            fieldConflictsRef.current = notice;
            dispatchAppEvent('seasuite:state-conflict', { conflicts: notice });
          }
          if (e?.httpStatus === 400 && /missing state/i.test(String(e?.message || ''))) {
            // A backend from before CONC-01: replay the whole-blob payload below.
            patchSupportedRef.current = false;
          }
          // Anything else (offline, 5xx) leaves the backup for the next boot.
        }
        if (patchSupportedRef.current) return;
      }

      // Read a verdict from classifyTeardownBackup and either write what it
      // recovered or say why nothing could be. Returns the payload+version to
      // write, or null when the outcome is already terminal.
      const planFrom = (verdict) => {
        if (verdict.outcome === 'already-stored') {
          // Our own teardown write is what moved the row. Nothing is lost, so
          // nothing is recovered and nothing is said.
          clearTeardownBackup(id, ownerUserId);
          return null;
        }
        if (verdict.outcome === 'unclassifiable') {
          // A backup written before GH #6607 carries no record of what the user
          // actually changed, so payload-vs-row differences cannot be told apart
          // from "the other writer deleted a record we still hold" — merging
          // would resurrect it. We cannot recover this edit, but we can refuse
          // to lose it in silence, which is the half that made this a §6.2
          // failure rather than an inconvenience.
          giveUp(
            `a change from your previous session (${describeStateKeys(verdict.missingKeys)}) ` +
              'may not have been saved — this workspace was changed elsewhere in the meantime and ' +
              'it could not be restored automatically. Check it before re-entering anything.'
          );
          return null;
        }
        if (!verdict.recoverable.length) {
          giveUp(unrecoveredMessage(verdict, { partial: false }));
          return null;
        }
        return { payload: verdict.payload, verdict };
      };

      // The row still holds exactly what it held when the backup was taken, so
      // nothing has to be reconciled: replay the payload as-is.
      let plan = { payload: backup.payload, verdict: null };
      let writeVersion = backup.version;

      if (String(backup.version) !== String(currentVersion)) {
        // GH #6607 — the row moved. That used to end here, with the backup
        // deleted and the user told nothing, on the reasoning that the mover was
        // either our own teardown write or a writer whose change we must not
        // clobber. Both halves of that are guesses, and the second is the
        // expensive kind: an unrelated writer bumping the row (a sweeper, a
        // document workflow, another module, another tab) was enough to discard
        // a whole unsaved edit — a GDPR Article 30 RoPA record among them — with
        // no banner and no server ever having been asked.
        //
        // The server already implements the answer: state-write refuses a stale
        // write with a 409 AND hands back the state that won, precisely so a
        // client can merge onto it rather than guess.
        plan = planFrom(
          classifyTeardownBackup(backup, stripBlobOwnedKeys(freshState || serverStateRef.current))
        );
        if (!plan) return;
        writeVersion = currentVersion;
      }

      const write = (payload, version) => {
        onWriteIssued();
        return withWriteDeadline(
          saveTenantStateVersioned(id, payload, version, {
            blobKeysOwnedByStores: BLOB_KEYS_OWNED_BY_STORES,
          })
        );
      };

      const succeeded = (verdict) => {
        clearTeardownBackup(id, ownerUserId);
        console.warn(
          '[usePersistence] Recovered an edit that failed to save when a previous tab/session closed.'
        );
        const message = verdict && unrecoveredMessage(verdict, { partial: true });
        // Part of the edit was merged back and part was not. Saying so is not
        // optional: the user is looking at a workspace that now holds some of
        // their previous session's work and not the rest.
        if (message) dispatchWriteErrorBanner(message);
      };

      try {
        await write(plan.payload, writeVersion);
        succeeded(plan.verdict);
      } catch (e) {
        if (e?.httpStatus !== 409) {
          // Still offline, a 5xx, anything else — the backup stays in place for
          // the next boot to retry.
          return;
        }
        // The row moved again between the boot read and this write. The refusal
        // carries the state that won (azureClient attaches it for exactly this,
        // GH #5358), so re-resolve against it rather than throwing the user's
        // only copy away on a first refusal.
        if (e.serverState === undefined) {
          // No snapshot — a backend older than #5358, or an assembly failure.
          // Leave the backup for the next boot, which re-reads the row and
          // classifies against it properly; say so rather than going quiet.
          dispatchWriteErrorBanner(
            'a change from your previous session could not be restored yet — this workspace was ' +
              'changed elsewhere while it was being restored. It will be retried the next time ' +
              'you open the app.'
          );
          return;
        }
        const retry = planFrom(classifyTeardownBackup(backup, stripBlobOwnedKeys(e.serverState)));
        if (!retry) return;
        try {
          await write(retry.payload, e.serverStateVersion ?? null);
          succeeded(retry.verdict);
        } catch (retryError) {
          if (retryError?.httpStatus === 409) {
            // Two refusals in a row against a writer that keeps winning. Stop
            // racing it, keep the backup for the next boot rather than
            // destroying it, and tell the user where things stand.
            dispatchWriteErrorBanner(
              'a change from your previous session could not be restored — this workspace is being ' +
                'changed elsewhere. It will be retried the next time you open the app; check it ' +
                'before re-entering anything.'
            );
          }
          // Any other failure leaves the backup in place for the next boot.
        }
      }
    },
    []
  );
  // Resolves true when a write was ISSUED, whatever became of it: any issued
  // write may have moved the row, so the boot path re-reads before the page is
  // made ready (GH #7014). Each write is bounded by withWriteDeadline, because
  // boot now waits on it.
  const replayOrphanedTeardownBackup = useCallback(
    async (id, currentVersion, ownerUserId, freshState) => {
      let issued = false;
      await replayTeardownBackupOnce(id, currentVersion, ownerUserId, freshState, () => {
        issued = true;
      });
      return issued;
    },
    [replayTeardownBackupOnce]
  );

  useEffect(() => {
    if (!tenantId || !session) return;
    // GH #6307 — claim ownership of any backup this page instance goes on to
    // write, before the read that would make one possible.
    backupOwnerRef.current = session.userId || null;
    const controller = new AbortController();
    setStateReady(false);
    setStateLoadError(null);

    // Retry up to 3 times for transient failures (e.g. a 502 from the Function
    // App), mirroring useTenant.js's registry-load retry contract, before
    // surfacing a load-failed state (#4915) instead of leaving the app stuck
    // on the boot splash screen forever with only a console.error.
    async function load() {
      let lastError = null;
      let replayAttempted = false;
      for (let attempt = 1; attempt <= 3; attempt++) {
        // The effect may have been torn down during the backoff sleep below.
        // Checked here rather than relying on the chained listener: adding an
        // 'abort' listener to a signal that has ALREADY aborted never fires,
        // so without this the next attempt would issue a live request nobody
        // is waiting for.
        if (controller.signal.aborted) return;
        // Per-attempt deadline, chained off the effect's own controller so an
        // unmount still cancels the read. Two different aborts arrive at the
        // same catch and mean opposite things: the effect's abort means nobody
        // is left to tell, so return silently; the deadline's abort means the
        // read did not answer, which is a failure this loop must retry and
        // ultimately report. Before this, both landed in a blanket
        // `e?.name === 'AbortError' → return`, so a hung read exited the loop
        // on attempt 1 leaving stateReady false and stateLoadError null — the
        // permanent splash screen (#6012 / #6149).
        const attemptController = new AbortController();
        const abortAttempt = () => attemptController.abort();
        controller.signal.addEventListener('abort', abortAttempt);
        const deadline = setTimeout(abortAttempt, STATE_LOAD_ATTEMPT_TIMEOUT_MS);
        try {
          const { state: loaded, stateVersion } = await loadTenantStateWithVersion(
            tenantId,
            attemptController.signal
          );
          if (controller.signal.aborted) return;
          let migrated = migrateState(loaded);
          let readyVersion = stateVersion;
          // GH #7014 — the replay is awaited BEFORE the page is ready. It
          // writes the row, so a page made interactive on the version read
          // above is one write behind: its first save is a guaranteed 409, and
          // a re-read fired afterwards lands on top of whatever the user did in
          // between. Starting the page from the row the replay produced leaves
          // no window for either. Costs one write and one read, only when a
          // backup exists.
          // At most once per boot: a re-read that fails below falls to the
          // retry, whose fresh read is then adopted without a second replay.
          const replay = !replayAttempted;
          replayAttempted = true;
          if (
            replay &&
            (await replayOrphanedTeardownBackup(tenantId, stateVersion, session.userId, migrated))
          ) {
            if (controller.signal.aborted) return;
            const reread = await loadTenantStateWithVersion(tenantId, attemptController.signal);
            if (controller.signal.aborted) return;
            migrated = migrateState(reread.state);
            readyVersion = reread.stateVersion;
          }
          serverStateRef.current = migrated;
          stateRef.current = migrated;
          stateVersionRef.current = readyVersion;
          setState(migrated);
          lastRefreshSuccessRef.current = Date.now();
          setStateReady(true);
          return;
        } catch (e) {
          if (controller.signal.aborted) return; // unmounted / tenant changed
          lastError = attemptController.signal.aborted
            ? new Error(
                `state-read did not answer within ${STATE_LOAD_ATTEMPT_TIMEOUT_MS}ms (attempt ${attempt})`
              )
            : e;
          if (attempt < 3) {
            await new Promise((r) => setTimeout(r, 1000 * attempt)); // 1s, then 2s
          }
        } finally {
          clearTimeout(deadline);
          controller.signal.removeEventListener('abort', abortAttempt);
        }
      }
      console.error(
        '[usePersistence] loadTenantState failed after retries, state not ready:',
        lastError?.message
      );
      setStateLoadError('load-failed');
    }
    load();

    return () => {
      controller.abort();
    };
  }, [tenantId, session, replayOrphanedTeardownBackup]);

  // GH #6148 — state-write reported key families it was handed and did not
  // write. Raise the write banner only when one of them actually carried a
  // local edit the server has not seen: that combination is a lost write (a
  // per-record write failed and the blob save is not a fallback for it), while
  // an unchanged echo is the ordinary case and must stay silent or the banner
  // becomes noise everyone learns to dismiss.
  const reportNotPersisted = useCallback((families, sentPayload) => {
    const diverged = divergedNotPersistedFamilies(families, sentPayload, serverStateRef.current);
    if (!diverged.length) return;
    dispatchWriteErrorBanner(
      `part of this change was not saved (${diverged.join(', ')}). ` +
        'The server stored the rest. Re-apply that change, or reload the page to see what is stored.'
    );
  }, []);

  // OPP004A-04 (GH #7167). state-write's guards restore a record instead of
  // rejecting the save, and stamp server-owned fields. Adopting our own
  // payload as the server's copy kept the rejected value on screen and
  // re-sent it — to be restored again — on every later save, with nothing
  // shown. So: the stored value becomes the server snapshot, the screen
  // settles on it unless the key was edited again while the write was in
  // flight (that newer edit's own save is judged on its own), and every
  // reason is told to the user in plain language.
  const settleRewritten = useCallback((rewritten, sentState) => {
    const keys = Object.keys(rewritten || {}).filter(
      (k) => rewritten[k] && 'value' in rewritten[k]
    );
    if (!keys.length) return;
    const nextServer = { ...serverStateRef.current };
    const adopt = {};
    for (const k of keys) {
      nextServer[k] = rewritten[k].value;
      if (sameJson(stateRef.current?.[k], sentState?.[k])) adopt[k] = rewritten[k].value;
    }
    if (stateRef.current === sentState) {
      // Nothing was edited while the write was in flight: the screen and the
      // server snapshot become the same object, so the save loop (which
      // compares by identity) has nothing left to send.
      const next = { ...sentState, ...adopt };
      serverStateRef.current = next;
      stateRef.current = next;
      setState(next);
    } else {
      serverStateRef.current = nextServer;
      if (Object.keys(adopt).length) {
        const next = { ...stateRef.current, ...adopt };
        stateRef.current = next;
        setState(next);
      }
    }
    const sentences = describeRewritten(rewritten);
    if (sentences.length) {
      dispatchWriteErrorBanner(sentences.join(' '));
      dispatchAppEvent('seasuite:state-rewritten', { keys, sentences });
    }
  }, []);

  // RDRC-04A: an employee record an employees-write ACTION has just committed
  // (record-acknowledgement) becomes the server's copy of that employee. Put
  // through updateState instead, the save cycle would diff it against the old
  // snapshot and post the whole record straight back — an echo write that a
  // concurrent edit to the same employee turns into a conflict the user never
  // caused.
  //
  // When this client has unsaved edits to the employee, the screen is a
  // three-way merge (TRYGGR_INTENT.md §13): base = the copy this screen last
  // had from the server, mine = the screen, theirs = the committed record,
  // which carries anything another user saved meanwhile. Edits to different
  // fields both land; a field both sides changed shows theirs on screen and is
  // named to the user with both values (the CONC-01 conflict notice), so the
  // next save can never overwrite another user's edit unseen.
  //
  // Each adoption is also remembered with a sequence number, so a save that
  // was already in flight cannot put its pre-adoption copy back as the
  // server's snapshot when it lands (reapplyAdoptedEmployees).
  const adoptedEmployeesRef = useRef(new Map());
  // The keep-mine listener is registered before updateState is declared.
  const updateStateRef = useRef(null);
  const adoptSeqRef = useRef(0);
  // CONC-01: employee fields the user chose "keep mine" on, as
  // { employeeId, path } — sent with the next save of that employee so the
  // server audits the overwrite, and dropped once that save lands.
  const employeeOverridesRef = useRef([]);
  // Consecutive employees-write 409s merged and re-sent without a save
  // landing in between. The first re-send is immediate; a run of them backs
  // off like any other failed write, so a record that keeps moving cannot
  // spin the save loop.
  const employeeConflictRunRef = useRef(0);
  const adoptServerEmployee = useCallback((committed) => {
    if (!committed?.id) return;
    adoptSeqRef.current += 1;
    adoptedEmployeesRef.current.set(committed.id, {
      record: committed,
      seq: adoptSeqRef.current,
    });
    const findIn = (s) =>
      Array.isArray(s?.employees) ? s.employees.find((e) => e && e.id === committed.id) : undefined;
    const replaceIn = (s, emp) =>
      Array.isArray(s?.employees) && s.employees.some((e) => e && e.id === emp.id)
        ? { ...s, employees: s.employees.map((e) => (e && e.id === emp.id ? emp : e)) }
        : s;
    const server = serverStateRef.current;
    const current = stateRef.current;
    const local = findIn(current);
    const base = findIn(server);
    const localUnchanged = sameJson(local, base);
    const nextServer = server ? replaceIn(server, committed) : server;
    if (!local) {
      serverStateRef.current = nextServer;
      return;
    }
    if (current === server && localUnchanged) {
      // Nothing unsaved at all: screen and snapshot stay the same object, so
      // the save loop (which compares by identity) has nothing to send.
      serverStateRef.current = nextServer;
      stateRef.current = nextServer;
      setState(nextServer);
      return;
    }
    let nextLocal = committed;
    let notice = [];
    if (!localUnchanged) {
      const merged = mergeEmployeeEdits({ base, mine: local, theirs: committed });
      nextLocal = merged.employee;
      notice = merged.conflicts;
    }
    serverStateRef.current = nextServer;
    const next = replaceIn(current, nextLocal);
    stateRef.current = next;
    setState(next);
    if (notice.length) {
      fieldConflictsRef.current = [...fieldConflictsRef.current, ...notice];
      dispatchAppEvent('seasuite:state-conflict', { conflicts: fieldConflictsRef.current });
    }
  }, []);

  // A save that started before an adoption and lands after it would make its
  // own (pre-adoption) payload the server snapshot, and the next diff would
  // post the old employee back over the acknowledgement. Re-apply every
  // adoption newer than the save; older ones are already inside its payload.
  const reapplyAdoptedEmployees = useCallback((startSeq) => {
    const adopted = adoptedEmployeesRef.current;
    if (!adopted.size) return;
    let server = serverStateRef.current;
    for (const [id, { record, seq }] of [...adopted]) {
      if (seq <= startSeq) {
        adopted.delete(id);
        continue;
      }
      if (Array.isArray(server?.employees) && server.employees.some((e) => e && e.id === id)) {
        server = {
          ...server,
          employees: server.employees.map((e) => (e && e.id === id ? record : e)),
        };
      }
    }
    serverStateRef.current = server;
  }, []);

  // RCQA-04: competence-gap tasks employees-write's reconcile committed to
  // state.adhocTasks. Adopted by id into the server snapshot and the screen,
  // so the Tasks page and the Competence section agree at once — and so the
  // next save diffs against them instead of never having seen them. Applied
  // whether or not the blob version is adopted (TRYGGR_INTENT.md §13): a
  // refused version adoption costs a 409 + merge, a dropped task would not
  // show until reload. Only competence-gap tasks are ever touched.
  const adoptedTasksRef = useRef(new Map());
  const adoptServerTasks = useCallback((tasks) => {
    const gap = (Array.isArray(tasks) ? tasks : []).filter(
      (t) => t && t.id && t.type === GAP_TASK_TYPE
    );
    if (!gap.length) return;
    for (const t of gap) {
      adoptSeqRef.current += 1;
      adoptedTasksRef.current.set(t.id, { record: t, seq: adoptSeqRef.current });
    }
    const server = serverStateRef.current;
    const current = stateRef.current;
    const nextServer = server ? upsertTasks(server, gap) : server;
    if (current === server) {
      serverStateRef.current = nextServer;
      stateRef.current = nextServer;
      setState(nextServer);
      return;
    }
    const localTasks = gap.map((committed) => {
      const local = findTask(current, committed.id);
      if (!local || sameJson(local, findTask(server, committed.id))) return committed;
      // Unsaved local edits (notes, owner, due date) stay; what only the
      // server may write comes from the committed copy.
      const merged = { ...local };
      for (const f of GAP_TASK_SERVER_OWNED_FIELDS) {
        if (f in committed) merged[f] = committed[f];
        else delete merged[f];
      }
      // Closing and reopening are the server's; open ⇄ in progress is the
      // person's, so a local status change survives unless either side is closed.
      if (committed.status === 'closed' || local.status === 'closed')
        merged.status = committed.status;
      return merged;
    });
    serverStateRef.current = nextServer;
    const next = upsertTasks(current, localTasks);
    stateRef.current = next;
    setState(next);
  }, []);

  // Same race as reapplyAdoptedEmployees: a save that started before a task
  // adoption must not make its pre-adoption payload the server snapshot.
  const reapplyAdoptedTasks = useCallback((startSeq) => {
    const adopted = adoptedTasksRef.current;
    if (!adopted.size) return;
    const newer = [];
    for (const [id, { record, seq }] of [...adopted]) {
      if (seq <= startSeq) adopted.delete(id);
      else newer.push(record);
    }
    if (newer.length && serverStateRef.current) {
      serverStateRef.current = upsertTasks(serverStateRef.current, newer);
    }
  }, []);

  const persistState = useCallback(
    (stateToSave, source = 'timer', opts = {}) => {
      // Honour a reconnect that arrived while this (doomed) write was in
      // flight: reset the failure run so the next attempt is scheduled at the
      // bottom of the backoff rather than up to 30s out. Called immediately
      // before every retryDelayFor() so the collapsed delay is the one actually
      // used, and after the write-error banner decision so a genuine failure is
      // still reported to the user.
      const consumeReconnectSignal = () => {
        if (!reconnectedWhileInFlightRef.current) return;
        reconnectedWhileInFlightRef.current = false;
        writeFailuresRef.current = 0;
      };
      // Save receipt for this payload (header note). undefined when the
      // snapshot did not come from updateState — an initial load, a merge we
      // have not tagged, a keepalive teardown write — in which case every
      // announce below is a no-op and behaviour is exactly as before.
      const coveredGeneration = snapshotGenerationsRef.current.get(stateToSave);
      const announceSaved = () => {
        if (coveredGeneration === undefined) return;
        dispatchAppEvent('seasuite:state-saved', { generation: coveredGeneration });
      };
      // `reason` is one of 'conflict' | 'session-expired' | 'unreachable' | 'refused'.
      // Announced only once an outcome is terminal for THIS attempt; a retry
      // that later lands announces success for the same generation, so a
      // listener that stays armed after a failure sees the correction.
      const announceFailed = (reason) => {
        if (coveredGeneration === undefined) return;
        dispatchAppEvent('seasuite:state-save-failed', {
          generation: coveredGeneration,
          reason,
        });
      };

      // None of the BLOB_OWNED_KEY_GROUPS families is owned by the blob: risk
      // by the per-record outbox, controls by controls-write, BC/DR by
      // bcdr-write, and — since GH #6148 — findings, AI governance, supplier
      // and vendors by their own endpoints too. Each carries its own
      // durability and its own error surface, so if this save's only change
      // vs. the last server snapshot is to those keys there is nothing for the
      // blob to write: adopt the projection and skip it, rather than rewriting
      // the ~90-key state column for an edit that never travels in it. When
      // other keys also changed, the write proceeds (with these stripped
      // below). keepalive saves (unload/beacon) skip this shortcut and always
      // write, so a queued unrelated change is never dropped on teardown.
      //
      // employees is ALSO one of the stripped keys (EMPA-04) but, unlike the
      // others, is not a fire-and-forget side channel this function can
      // ignore — its own write (saveEmployeeRecords, below) lives inside THIS
      // same call. A "blob has nothing new" verdict must not skip that write,
      // so an employees-only change always falls through past this shortcut
      // even when the blob-stripped comparison would otherwise call it settled.
      if (
        !opts.keepalive &&
        serverStateRef.current &&
        diffEmployeesForWrite(serverStateRef.current, stateToSave).length === 0
      ) {
        const blobNow = stripBlobOwnedKeys(stateToSave);
        const blobServer = stripBlobOwnedKeys(serverStateRef.current);
        // Only relevant when these keys are present on either side (else the
        // strip returned the same reference and there is nothing special here).
        if (blobNow !== stateToSave || blobServer !== serverStateRef.current) {
          try {
            if (JSON.stringify(blobNow) === JSON.stringify(blobServer)) {
              serverStateRef.current = stateToSave;
              logDiag('persist.skip', { source, reason: 'blob-owned-key-only-change' });
              // Nothing for the blob to write: the only changed keys are owned
              // by the risk outbox / controls-write, which carry their own
              // durability and their own error surface. From the blob's point
              // of view this edit is settled, so the receipt says so.
              announceSaved();
              return Promise.resolve();
            }
          } catch {
            /* serialisation failure — fall through to a normal write */
          }
        }
      }
      logDiag('persist.start', {
        source,
        stateBytes: approxBytes(stateToSave),
        flags: {
          localDirty: localDirtyRef.current,
          pending: !!pendingState.current,
          inFlight: inFlightSaveRef.current,
        },
      });
      inFlightSaveRef.current = true;
      const startedAt = Date.now();
      // Snapshot the server state before the write so we can diff it against
      // the fresh state if a 409 occurs (identifies what the external writer changed).
      const preSaveServerState = serverStateRef.current;
      const adoptSeqAtStart = adoptSeqRef.current;
      // Snapshot the edit generation stateToSave corresponds to, so a 409
      // resolution below can detect a newer local edit racing its reload.
      const startGeneration = localGenerationRef.current;

      // The blob payload never carries risk, control, or BC/DR keys — they
      // are authoritative in tenant_risk_state / dbo.tenant_control_entities /
      // dbo.tenant_bcdr_entities and owned by the outbox (ADR-0001) /
      // controls-write API (ADR-0004) / bcdr-write API (ADR-0006).
      // state-write restores them from its own row when the client omits them
      // and the corresponding row does not yet exist, so an un-migrated
      // tenant is never wiped.
      const blobPayload = stripBlobOwnedKeys(stateToSave);

      // OPP004A-04 (GH #7167): what state-write stored for a guarded key, when
      // it differs from what this payload sent. Captured here, applied once
      // the write is acknowledged (below).
      let rewritten = null;
      const onRewritten = (r) => {
        rewritten = r;
      };

      // Guard: when we loaded with no version (row was absent at load time), a
      // concurrent seed or super-admin write may have created/populated the row
      // since then.  A null-version write bypasses the state_version check on the
      // server and would silently overwrite the seed.  Pre-fetch the current
      // version before committing; if the row now exists, treat it like a 409
      // (reload + schedule a retry so local changes are not lost).
      //
      // `version` is read HERE rather than at the top of the save, because a
      // concurrent write elsewhere (another tab, a sweeper) could otherwise
      // move it in the gap between save start and this point. (Pre-EMPA-04,
      // the employees write below also bumped this version by writing its
      // own blob copy; it no longer touches tenant_state at all, so that is
      // no longer a reason — but reading late is still correct on its own.)
      const startBlobWrite = () => {
        // CONC-01 (GH #7817): send only what this tab changed since the state
        // it last confirmed with the server, as field-level operations the
        // server applies to the CURRENT row. Nothing another session, a
        // writer endpoint or a server hook stored in between can refuse it,
        // and nothing this tab did not change is sent to overwrite it. The
        // whole-blob write below stays for the one case with no baseline to
        // diff against (the very first save of a fresh load path) and for a
        // backend that does not yet take patches.
        if (preSaveServerState && patchSupportedRef.current) {
          const ops = diffState(stripBlobOwnedKeys(preSaveServerState), blobPayload);
          if (!ops.length) return Promise.resolve(stateVersionRef.current);
          return Promise.resolve(
            saveTenantStatePatch(tenantId, ops, {
              keepalive: !!opts.keepalive,
              onNotPersisted: (families) => reportNotPersisted(families, blobPayload),
              onRewritten,
            })
          ).catch((e) => {
            if (e?.httpStatus === 400 && /missing state/i.test(String(e?.message || ''))) {
              // A backend from before CONC-01. Fall back for this session.
              patchSupportedRef.current = false;
              return startBlobWrite();
            }
            if (e?.httpStatus === 409 && e?.conflictReason !== 'field-conflict') {
              // The row kept moving under every server-side attempt. Nothing
              // was written and re-sending the same patch is safe, so this is
              // a transient failure, not a conflict with anyone's edit.
              const busy = new Error('State busy — retrying');
              busy.httpStatus = 503;
              throw busy;
            }
            throw e;
          });
        }
        const version = stateVersionRef.current;
        return version !== null
          ? Promise.resolve(
              saveTenantStateVersioned(tenantId, blobPayload, version, {
                keepalive: !!opts.keepalive,
                blobKeysOwnedByStores: BLOB_KEYS_OWNED_BY_STORES,
                onNotPersisted: (families) => reportNotPersisted(families, blobPayload),
                onRewritten,
              })
            )
          : loadTenantStateWithVersion(tenantId)
              .then(({ stateVersion: freshVersion, state: freshState }) => {
                if (freshVersion !== null) {
                  // Row was created concurrently — update our version snapshot and
                  // surface a synthetic conflict so the catch block below handles
                  // the retry.  Mark versionAlreadyReloaded so the 409 handler
                  // skips a redundant re-fetch.
                  stateVersionRef.current = freshVersion;
                  serverStateRef.current = migrateState(freshState);
                  const conflict = new Error('Concurrent seed detected — reloading');
                  conflict.httpStatus = 409;
                  conflict.versionAlreadyReloaded = true;
                  throw conflict;
                }
                // Row still absent — proceed with the null-version write (migration /
                // first-write path, e.g. rows not yet pre-created at signup).
                return saveTenantStateVersioned(tenantId, blobPayload, null, {
                  keepalive: !!opts.keepalive,
                  blobKeysOwnedByStores: BLOB_KEYS_OWNED_BY_STORES,
                  onNotPersisted: (families) => reportNotPersisted(families, blobPayload),
                  onRewritten,
                });
              })
              .catch((e) => {
                // Re-throw so the outer catch block below handles the 409.
                throw e;
              });
      };

      // ── Employees: per-record write, ahead of the blob (ADR-0008 §2.4) ─────
      // Employee records live in dbo.tenant_employees, one row each with its
      // own ROWVERSION, and state-read serves those rows in preference to the
      // blob's copy once a tenant has been split. A change that reached the
      // server only inside this blob payload would therefore be written and
      // then never read again — so every employee change has to travel through
      // employees-write, whichever component made it.
      //
      // Doing the diff HERE rather than in each component is what makes that
      // "whichever component" true: EmployeesPage's bulk actions, the
      // lifecycle wizard, the detail modal and every future call site all end
      // up in this one save cycle, and none of them can forget. It is also
      // exactly what ADR-0008 §2.4 asks for — diff against the last
      // server-confirmed shape, post only the records that changed, adopt the
      // versions the response returns (employeeApi does that part).
      //
      // Ahead of the blob write, not after it: a failure here fails the whole
      // save, so serverStateRef is not advanced and the next cycle re-diffs
      // and retries. Landing the blob first and failing after would leave the
      // change looking saved while the rows — the copy that is actually read —
      // never got it.
      //
      // Deletions are NOT inferred from this diff. They are issued explicitly,
      // by id, by usePeopleActions.deleteEmployee (GH #3903 / ADR-0002 §3.5).
      //
      // EMPA-04: employees-write writes dbo.tenant_employees, not the blob's
      // employees key. RCQA-04: its competence-gap reconcile may commit
      // state.adhocTasks, and then returns the tasks and the blob version —
      // the transport adopts the version (tenantStateVersion.js) and the
      // tasks are adopted below as soon as employees-write answers. `employees` is also stripped
      // from blobPayload above (EMPLOYEE_BLOB_KEYS), so the per-record write
      // and the blob write are now fully independent; this call is kept
      // ahead of the blob write only to preserve ADR-0008 section 2.4's
      // "diff runs first, whole save fails together" behaviour.
      const changedEmployees = diffEmployeesForWrite(preSaveServerState, stateToSave);
      // Overrides queued before this save started ride it; one queued while it
      // is in flight waits for the next. A queued override whose employee this
      // save does not carry overwrote nothing, so it is dropped with the rest.
      const overridesAtStart = changedEmployees.length ? [...employeeOverridesRef.current] : [];
      const changedIds = new Set(changedEmployees.map((e) => e && e.id));
      const overrides = overridesAtStart.filter((o) => changedIds.has(o.employeeId));
      const writePromise = changedEmployees.length
        ? saveEmployeeRecords(changedEmployees, tenantId, overrides.length ? { overrides } : {})
            .catch((err) => {
              // Marks a refusal of the per-record write, so the 409 below is
              // merged per employee field rather than by the blob's handler.
              if (err && typeof err === 'object') err.employeeWrite = true;
              throw err;
            })
            .then((res) => {
              employeeOverridesRef.current = employeeOverridesRef.current.filter(
                (o) => !overridesAtStart.includes(o)
              );
              // RCQA-04: adopted now, not when the whole save lands — a blob
              // write that fails after this would otherwise drop them, and the
              // retry's reconcile is a no-op that returns none. A landing save
              // keeps them via reapplyAdoptedTasks (their seq is newer).
              adoptServerTasks(res?.tasks);
              return res;
            })
            .then(startBlobWrite)
        : startBlobWrite();

      return withWriteDeadline(Promise.resolve(writePromise))
        .then((newVersion) => {
          // Acknowledged — whatever we were unsure about is settled.
          unverifiedWriteRef.current = null;
          employeeConflictRunRef.current = 0;
          serverStateRef.current = stateToSave;
          reapplyAdoptedEmployees(adoptSeqAtStart);
          reapplyAdoptedTasks(adoptSeqAtStart);
          if (rewritten) settleRewritten(rewritten, stateToSave);
          // Refresh our cached version with the ROWVERSION the server produced.
          // state_version increments on every successful write; without adopting
          // it here the NEXT write would send a stale version and get a
          // guaranteed 409 — a self-inflicted conflict for a single user
          // (the delete-then-resurrect + spurious "Workspace updated elsewhere"
          // banner). null means the row was absent (no-op write) — keep the
          // existing null so the first-write guard path still runs.
          if (newVersion != null) stateVersionRef.current = newVersion;
          // The write landed — end any run of retryable failures and take the
          // "changes may not have been saved" banner down, since it is no
          // longer true and leaving it up is its own kind of wrong answer.
          writeFailuresRef.current = 0;
          // Whatever a terminal commit was waiting to have written has now been
          // written; a stale request must not make the NEXT ordinary edit skip
          // its debounce.
          commitRequestedRef.current = false;
          // A landed write also ends any run of deferred conflict resolutions —
          // whatever was racing has now been carried to the server.
          conflictDeferralsRef.current = 0;
          conflictRunStartedAtRef.current = 0;
          // Connectivity is demonstrably fine — a pending reconnect signal has
          // nothing left to accelerate and must not leak into a later,
          // unrelated failure's backoff.
          reconnectedWhileInFlightRef.current = false;
          if (writeErrorShownRef.current) {
            writeErrorShownRef.current = false;
            // Also drops a notice parked for a banner that has not mounted yet
            // (writeErrors.js) — the event alone only clears a VISIBLE banner.
            clearPendingWriteErrorNotice();
            dispatchAppEvent('seasuite:write-recovered');
          }
          logDiag('persist.settle', { durationMs: Date.now() - startedAt, ok: true });
          announceSaved();
        })
        .catch((e) => {
          logDiag('persist.settle', {
            durationMs: Date.now() - startedAt,
            ok: false,
            errorMessage: String(e?.message || e).slice(0, 200),
          });
          if (e?.httpStatus === 409 && e?.conflictReason === 'field-conflict') {
            // CONC-01 — a field this save changed was changed to something
            // else by another session. Nothing of this save was written (the
            // server applies a patch all-or-nothing). Everything that did not
            // clash is rebased onto the state that won and re-sent at once;
            // each clash is shown with both values and the user's own value
            // kept in the notice, with "keep mine" where that is safe
            // (patchConflicts.js). No other refusal reaches this branch: a
            // change anywhere else in the tenant no longer refuses a save.
            inFlightSaveRef.current = false;
            const freshPromise =
              e.serverState !== undefined && e.serverState !== null
                ? Promise.resolve({
                    fresh: migrateState(e.serverState),
                    freshVersion: e.serverStateVersion ?? null,
                  })
                : loadTenantStateWithVersion(tenantId).then(({ state: st, stateVersion: v }) => ({
                    fresh: migrateState(st),
                    freshVersion: v,
                  }));
            freshPromise
              .then(({ fresh, freshVersion }) => {
                // A newer local edit landed while this was in flight: rebase
                // THAT, so it is carried forward rather than dropped.
                const latest =
                  localGenerationRef.current !== startGeneration ? stateRef.current : stateToSave;
                const { rebased, conflicts } = rebaseOnConflict({
                  preSave: preSaveServerState,
                  toSave: latest,
                  fresh,
                  strip: stripBlobOwnedKeys,
                });
                serverStateRef.current = fresh;
                if (freshVersion != null) stateVersionRef.current = freshVersion;
                stateRef.current = rebased;
                setState(rebased);
                const notice = conflicts.map((c) => describeConflict(c, fresh));
                logDiag('conflict.field', {
                  source,
                  fields: notice.length,
                  keys: [...new Set(notice.map((c) => c.path[0]))],
                });
                if (notice.length) {
                  fieldConflictsRef.current = notice;
                  dispatchAppEvent('seasuite:state-conflict', { conflicts: notice });
                  announceFailed('conflict');
                }
                if (saveTimer.current) {
                  clearTimeout(saveTimer.current);
                  saveTimer.current = null;
                }
                pendingState.current = null;
                localDirtyRef.current = false;
                persistState(rebased, 'conflict-rebase').catch(() => {
                  localDirtyRef.current = true;
                });
              })
              .catch((reloadErr) => {
                console.error(
                  '[usePersistence] field-conflict reload failed:',
                  reloadErr?.message || reloadErr
                );
                if (localGenerationRef.current === startGeneration) {
                  pendingState.current = stateToSave;
                  localDirtyRef.current = true;
                }
                writeFailuresRef.current += 1;
                if (!saveTimer.current && !inFlightSaveRef.current) {
                  saveTimer.current = setTimeout(() => {
                    saveTimer.current = null;
                    if (!pendingState.current || inFlightSaveRef.current) return;
                    const st = pendingState.current;
                    pendingState.current = null;
                    localDirtyRef.current = false;
                    persistState(st, 'conflict-reload-retry').catch(() => {
                      localDirtyRef.current = true;
                    });
                  }, retryDelayFor(writeFailuresRef.current));
                }
              });
            return;
          }
          if (e?.httpStatus === 409 && e?.employeeWrite) {
            // §13 case (c) — another user saved an employee this save carries
            // (a stale per-record version). Nothing of this save was written:
            // employees-write refused it before the blob write started. The
            // blob handler below would revert the whole `employees` key, so
            // one of two users editing different fields of one person would
            // lose their edit. Instead re-read the employees (which also
            // adopts their current versions) and merge each one that moved
            // three ways — base the screen loaded, the user's edits, the
            // server's record: fields only one side changed both land, a
            // field both changed is shown with both values (keep theirs /
            // keep mine), and the merged records are re-sent.
            inFlightSaveRef.current = false;
            employeeConflictRunRef.current += 1;
            const run = employeeConflictRunRef.current;
            // A refusal that keeps coming back is told to the user, as the
            // blob path's merge-retry ceiling does; the edit stays queued and
            // keeps being re-sent.
            if (run === CONFLICT_DEFERRAL_BANNER_AFTER) {
              reportConflictBanner('employee-conflict-ceiling', {
                source,
                deferrals: run,
                overlappingKeys: ['employees'],
              });
              dispatchAppEvent('seasuite:state-conflict');
              announceFailed('conflict');
            }
            const resend = (why) => {
              if (saveTimer.current) {
                clearTimeout(saveTimer.current);
                saveTimer.current = null;
              }
              // One write in flight at a time: a newer edit's save may have
              // started during the re-read; it picks this up when it settles.
              if (inFlightSaveRef.current) {
                pendingState.current = stateRef.current;
                localDirtyRef.current = true;
                return;
              }
              pendingState.current = null;
              localDirtyRef.current = false;
              persistState(stateRef.current, why).catch(() => {
                localDirtyRef.current = true;
              });
            };
            loadTenantStateWithVersion(tenantId)
              .then(({ state: st }) => {
                const fresh = migrateState(st);
                const base = serverStateRef.current;
                const moved = (Array.isArray(fresh?.employees) ? fresh.employees : []).filter(
                  (theirs) => {
                    const was = Array.isArray(base?.employees)
                      ? base.employees.find((x) => x && theirs && x.id === theirs.id)
                      : undefined;
                    return was && !sameJson(was, theirs);
                  }
                );
                const beforeMerge = stateRef.current;
                const receipt =
                  snapshotGenerationsRef.current.get(beforeMerge) ?? coveredGeneration;
                for (const theirs of moved) adoptServerEmployee(theirs);
                // Carry the receipt onto the merged snapshot, so the re-send
                // announces success for the generation the user is waiting on.
                if (receipt !== undefined && stateRef.current !== beforeMerge) {
                  snapshotGenerationsRef.current.set(stateRef.current, receipt);
                }
                logDiag('conflict.employee', {
                  source,
                  reason: e.conflictReason || null,
                  merged: moved.length,
                  run,
                });
                if (run === 1) {
                  resend('employee-conflict-merge');
                  return;
                }
                writeFailuresRef.current += 1;
                // The re-send carries the latest screen, so a pending debounce
                // is folded into it; resend parks it if a save is in flight.
                if (saveTimer.current) clearTimeout(saveTimer.current);
                saveTimer.current = setTimeout(() => {
                  saveTimer.current = null;
                  resend('employee-conflict-retry');
                }, retryDelayFor(writeFailuresRef.current));
              })
              .catch((reloadErr) => {
                console.error(
                  '[usePersistence] employee-conflict reload failed:',
                  reloadErr?.message || reloadErr
                );
                if (localGenerationRef.current === startGeneration) {
                  pendingState.current = stateToSave;
                  localDirtyRef.current = true;
                }
                writeFailuresRef.current += 1;
                if (!saveTimer.current && !inFlightSaveRef.current) {
                  saveTimer.current = setTimeout(() => {
                    saveTimer.current = null;
                    if (!pendingState.current || inFlightSaveRef.current) return;
                    pendingState.current = null;
                    localDirtyRef.current = false;
                    persistState(stateRef.current, 'employee-conflict-reload-retry').catch(() => {
                      localDirtyRef.current = true;
                    });
                  }, retryDelayFor(writeFailuresRef.current));
                }
              });
            return;
          }
          if (e?.httpStatus === 409) {
            // This write is finished — it was refused. Say so now rather than
            // waiting for .finally(), which is queued AFTER the resolution
            // below and so still reads "in flight" while it runs. That mattered
            // the moment the resolution stopped needing a network round trip of
            // its own (the e.serverState path added for GH #5358): the retry
            // found the flag still true, declined to start, and fell through to
            // the post-flight follow-up — putting a fresh 1.5s debounce in
            // front of an already-merged payload and making the recovery slower
            // than the reload it replaced. .finally() sets the same flag to the
            // same value a moment later, so this only tells the truth earlier;
            // the reload path already ran detached with the flag cleared (see
            // the race guard in the file header), so nothing new can race here.
            inFlightSaveRef.current = false;
            // A concurrent writer (another tab, a sweeper, or a super-admin push)
            // committed first. Load fresh state, then decide whether this is a
            // genuine human conflict (overlapping keys) or a false conflict
            // (sweeper / user in a different module touched unrelated keys).
            //
            // Genuine conflict (overlapping keys): adopt fresh state and show the
            // banner — the user must re-apply their changes. DB-CONC-001 applies.
            //
            // No-overlap: silently merge the user's changed keys onto the fresh
            // state and queue a retry — the user never sees a banner.
            //
            // versionAlreadyReloaded: the null-version guard above already fetched
            // fresh state and updated the refs — skip the redundant load. The
            // refs are deliberately NOT adopted here (see below) — only once we
            // know this resolution is not stale relative to a racing edit.
            //
            // e.serverState: the refusal itself carried the state that won
            // (GH #5358). state-write assembles it through the same code path
            // state-read uses, so it is the shape this resolution expects and
            // the reload below is pure latency — a whole extra round trip
            // during which the user's edit exists only in this browser. Absent
            // against a backend deployed before that change, and absent when
            // the server could not assemble it, so the reload stays the
            // fallback rather than being removed.
            const conflictCarriedState =
              !e.versionAlreadyReloaded && e.serverState !== undefined && e.serverState !== null;
            const reloadPromise = e.versionAlreadyReloaded
              ? Promise.resolve({
                  fresh: serverStateRef.current,
                  freshVersion: stateVersionRef.current,
                })
              : conflictCarriedState
                ? Promise.resolve({
                    fresh: migrateState(e.serverState),
                    freshVersion: e.serverStateVersion ?? null,
                  })
                : loadTenantStateWithVersion(tenantId).then(
                    ({ state: fresh, stateVersion: freshVersion }) => ({
                      fresh: migrateState(fresh),
                      freshVersion,
                    })
                  );
            reloadPromise
              .then(({ fresh, freshVersion }) => {
                if (!fresh) {
                  // No usable snapshot came back, so the resolution below cannot
                  // run at all. Returning here would strand the edit: pendingState
                  // and localDirtyRef were cleared when this write started, so
                  // nothing is left to re-save it and nothing tells the user
                  // (#5037). This is the same situation as a reload that threw —
                  // hand it to that handler, which re-queues the payload, arms the
                  // backoff, and raises the write-error banner if it keeps failing.
                  throw new Error('conflict reload returned no state');
                }

                // A newer local edit landed while this reload was in flight
                // (see the file header race-guard note). Applying THIS
                // resolution's stateToSave-derived merge/overwrite now would
                // silently discard that edit, so skip it — including adopting
                // this fetch's serverStateRef/stateVersionRef, since a later,
                // more authoritative save may already have superseded them
                // (adopting stale values here would just cause it to
                // self-conflict on its next write). The newer edit's own save
                // — already scheduled by the usual dirty/pending machinery —
                // carries the edit forward and re-syncs those refs itself.
                // Without a pre-save baseline getChangedTopLevelKeys returns []
                // for BOTH sides, so nothing classifies as user-changed, the merge
                // below produces no surviving change, and `fresh` is adopted
                // wholesale — discarding the edit with no trace. We cannot tell
                // what the user changed, so we cannot claim it survived: treat the
                // whole resolution as unresolvable and say so (#5037).
                //
                // Computed BEFORE the deferral check below, and touching no refs,
                // because whether the user is told must not depend on whether an
                // unrelated edit happened to land while this reload was in
                // flight (#5243).
                const canClassify = !!preSaveServerState;

                // A write that failed without an answer (dropped connection,
                // timeout, 5xx) may well have committed with only the response
                // lost. When it did, our cached version is stale, every retry
                // is refused, and this resolution sees the row holding values
                // it did not put there — so it reported OUR OWN write as
                // another session's, and told the user their work was not saved
                // when the row already held it (GH #5191).
                //
                // A key whose server value is exactly what that unacknowledged
                // write was carrying is that write landing late, not a second
                // writer, so it is not evidence of a conflict. Narrowing here
                // rather than at the banner keeps the merge honest too: those
                // keys stop being treated as external changes to reconcile
                // against. Anything the row holds that we did NOT send is still
                // a genuine external change and still banners.
                const ownLostWrite = unverifiedWriteRef.current;
                const externalChangedKeys = getChangedTopLevelKeys(
                  preSaveServerState,
                  fresh
                ).filter((k) => {
                  if (!ownLostWrite || !(k in (ownLostWrite.payload || {}))) return true;
                  return !sameJson(fresh?.[k], ownLostWrite.payload[k]);
                });
                const userChangedKeys = getChangedTopLevelKeys(preSaveServerState, stateToSave);

                // For each key that both sides changed, check at record granularity.
                // hasRecordLevelOverlap returns a quad-state:
                //   true          → genuine same-record conflict → banner
                //   'engine-only' → same record, but only engine-owned fields
                //                   changed externally → silent merge with
                //                   engine-field preservation (no banner)
                //   false         → different records changed → safe merge
                //   null          → cannot determine → conservative: treat as overlap
                const overlapResults = new Map();
                for (const k of externalChangedKeys) {
                  if (!userChangedKeys.includes(k)) continue;
                  overlapResults.set(
                    k,
                    hasRecordLevelOverlap(k, preSaveServerState?.[k], fresh?.[k], stateToSave?.[k])
                  );
                }

                // Real overlaps: true (confirmed) or null (cannot determine —
                // conservative fallback).  'engine-only' and false do not trigger
                // the banner.
                const overlappingKeys = [...overlapResults.entries()]
                  .filter(([, v]) => v !== false && v !== 'engine-only')
                  .map(([k]) => k);

                const hasOverlap = overlappingKeys.length > 0;

                // Per-key overlap classification (top-level key names only — no PII).
                // Values: 'confirmed' | 'engine-only' | 'different-records' | 'unknown'
                //
                // Computed here rather than at the banner site so EVERY report
                // carries it. It used to be built further down, after the
                // deferral-ceiling site had already reported and returned — so
                // the report for a resolution that never settled said only
                // which keys overlapped, never whether the classifier had
                // confirmed a same-record collision or merely failed to rule
                // one out. Those are the two different defects this capture
                // exists to tell apart.
                const overlapDetail = {};
                for (const [k, v] of overlapResults) {
                  overlapDetail[k] =
                    v === true
                      ? 'confirmed'
                      : v === false
                        ? 'different-records'
                        : v === 'engine-only'
                          ? 'engine-only'
                          : 'unknown';
                }

                // A newer local edit landed while this reload was in flight
                // (see the file header race-guard note). Applying THIS
                // resolution's stateToSave-derived merge/overwrite now would
                // silently discard that edit, so skip it — including adopting
                // this fetch's serverStateRef/stateVersionRef, since a later,
                // more authoritative save may already have superseded them
                // (adopting stale values here would just cause it to
                // self-conflict on its next write). The newer edit's own save
                // — already scheduled by the usual dirty/pending machinery —
                // carries the edit forward and re-syncs those refs itself.
                if (localGenerationRef.current !== startGeneration) {
                  // Deferring the RESOLUTION is correct. Deferring the
                  // ANSWER TO THE USER is not, and that is the gap #5243
                  // reports: on a genuine same-record collision the server had
                  // already refused the write and the edit was already lost,
                  // but the banner only fired if no unrelated updateState()
                  // happened to land during the reload — up to three times.
                  // Whether the user is told became a matter of timing.
                  //
                  // The classification above needs none of this cycle's refs,
                  // so it is just as valid here: if it says the user's own
                  // change did not survive, say so now. If it says every
                  // changed key merges safely, this is the ordinary recovered
                  // race #3594 covers and staying silent is right — bannering
                  // it would be pure noise.
                  const lostOnDefer = hasOverlap || !canClassify;
                  if (conflictDeferralsRef.current === 0) {
                    conflictRunStartedAtRef.current = startedAt;
                  }
                  conflictDeferralsRef.current += 1;
                  const runMs = Date.now() - conflictRunStartedAtRef.current;
                  const deferralsExhausted =
                    conflictDeferralsRef.current >= CONFLICT_DEFERRAL_BANNER_AFTER ||
                    runMs >= CONFLICT_DEFERRAL_BANNER_AFTER_MS;
                  logDiag('conflict.skip-raced-local-edit', {
                    source,
                    deferrals: conflictDeferralsRef.current,
                    runMs,
                    deferralsExhausted,
                    lostOnDefer,
                    overlappingKeys,
                    unclassifiable: !canClassify,
                  });
                  if (lostOnDefer || deferralsExhausted) {
                    // Reported BEFORE the counters are reset — how many cycles
                    // the resolution failed to settle for is the fact that
                    // makes this diagnosable, and reading it afterwards records
                    // zero every time.
                    reportConflictBanner('deferral-ceiling', {
                      source,
                      deferrals: conflictDeferralsRef.current,
                      runMs,
                      lostOnDefer,
                      overlappingKeys,
                      unclassifiable: !canClassify,
                      overlapDetail,
                    });
                    conflictDeferralsRef.current = 0;
                    conflictRunStartedAtRef.current = 0;
                    dispatchAppEvent('seasuite:state-conflict');
                    announceFailed('conflict');
                  }
                  return;
                }

                // NOT reset here. "This cycle resolves" is not the same thing
                // as "the edit reached the server": since #5454 the same counter
                // also tracks clean merge-and-retry cycles, and those are counted
                // BELOW, inside this very branch. Zeroing here first made that
                // increment unreachable — the counter climbed to 1, was reset to
                // 0 by the next cycle's pass through this line, and could never
                // cross CONFLICT_DEFERRAL_BANNER_AFTER however long the run went
                // on (#5429). A run ends where it actually ends: an acknowledged
                // write (see the .then above), or a cycle that reverted a key /
                // could not classify, which banners on its own and resets there.
                stateVersionRef.current = freshVersion;
                serverStateRef.current = fresh;

                // Stale-window proxy: how long ago did we last apply fresh server state?
                const staleMsAtConflict = lastRefreshSuccessRef.current
                  ? Date.now() - lastRefreshSuccessRef.current
                  : null;

                // #4904/#4905: resolve EACH user-changed key independently rather
                // than an all-or-nothing decision keyed off whether ANY key
                // overlaps. The old code took `!hasOverlap` as a single global
                // switch — the moment even ONE changed key came back `true` or
                // `null` (unresolvable, e.g. a genuinely unregistered collection),
                // it discarded every other user-changed key too, including ones
                // that resolved perfectly safely (`false`/`engine-only`). A
                // trainingPrograms conflict could silently wipe an unrelated
                // employees addition saved in the same debounced batch, and vice
                // versa. Each key now keeps its own outcome: a genuine/unresolvable
                // conflict reverts ONLY that key to the fresh server value (and
                // still raises the banner, since that field must be re-applied by
                // hand); every other key that safely merges (or that only the user
                // touched) survives and is queued for retry.
                logDiag(hasOverlap ? 'conflict.partial-merge' : 'conflict.silent-merge', {
                  source,
                  staleMsAtConflict,
                  externalChangedKeys,
                  userChangedKeys,
                  overlapDetail,
                });
                const merged = { ...fresh };
                let anyMergedChange = false;
                // Keys whose user-side change was thrown away by this resolution.
                // The banner is raised from THIS list rather than from a separately
                // computed overlap flag, so the thing that decides whether the user
                // is told is the thing that actually happened to their edit (#5037).
                const revertedKeys = [];
                for (const k of userChangedKeys) {
                  if (externalChangedKeys.includes(k)) {
                    const overlap = overlapResults.get(k);
                    if (overlap === true || overlap === null) {
                      // Genuine same-record collision, or unresolvable at record
                      // granularity — revert ONLY this key to the fresh server
                      // value; merged[k] already holds fresh[k]. The user must
                      // re-apply this specific change; the banner below tells them.
                      revertedKeys.push(k);
                      continue;
                    }
                    // 'engine-only' or false — safe to merge. Use engine carve-out
                    // merge for engine-only overlaps, standard merge otherwise. Fall
                    // back to taking the user's whole value when a granular merge is
                    // not possible (same behaviour as the old top-level merge).
                    const granular =
                      overlap === 'engine-only'
                        ? mergeWithEngineCarveOut(
                            k,
                            fresh[k],
                            stateToSave[k],
                            preSaveServerState?.[k]
                          )
                        : mergeCollectionKey(k, fresh[k], stateToSave[k], preSaveServerState?.[k]);
                    merged[k] = granular !== null ? granular : stateToSave[k];
                    anyMergedChange = true;
                  } else {
                    // Only the user changed this key — take it wholesale.
                    merged[k] = stateToSave[k];
                    anyMergedChange = true;
                  }
                }

                // This cycle's merge looks clean (nothing reverted) and is about
                // to be retried — but the retry can itself lose the same race
                // again, and again, if it is up against a fast-moving external
                // writer. Each such cycle "resolves" through this branch rather
                // than the raced-local-edit branch above (see that branch's
                // #5243/#5429 note: a same-tick conflictCarriedState reload
                // settles before this generation check can ever miss, so this
                // is now the ONLY branch a repeated-conflict run actually takes)
                // — so it must do its own deferral counting, or a run that never
                // durably writes stays silent forever regardless of
                // CONFLICT_DEFERRAL_BANNER_AFTER. A cycle that reverted a key or
                // couldn't classify already bannered below and resets the count;
                // only a clean-looking merge-and-retry accumulates here.
                let ceilingCrossed = false;
                if (anyMergedChange && revertedKeys.length === 0 && canClassify) {
                  if (conflictDeferralsRef.current === 0) {
                    conflictRunStartedAtRef.current = startedAt;
                  }
                  conflictDeferralsRef.current += 1;
                  // Either ceiling ends the run. The cycle count catches a fast
                  // server refusing repeatedly; the elapsed time catches a slow
                  // one, where three round trips can take the better part of a
                  // minute and counting alone would keep the user uninformed
                  // for all of it (#5661, #5704).
                  const runMs = Date.now() - conflictRunStartedAtRef.current;
                  ceilingCrossed =
                    conflictDeferralsRef.current >= CONFLICT_DEFERRAL_BANNER_AFTER ||
                    runMs >= CONFLICT_DEFERRAL_BANNER_AFTER_MS;
                  logDiag('conflict.merge-retry-deferral', {
                    source,
                    deferrals: conflictDeferralsRef.current,
                    runMs,
                    deferralsExhausted: ceilingCrossed,
                  });
                  if (ceilingCrossed) {
                    // Before the reset, for the same reason as the site above.
                    reportConflictBanner('merge-retry-ceiling', {
                      source,
                      deferrals: conflictDeferralsRef.current,
                      runMs,
                      overlapDetail,
                    });
                    conflictDeferralsRef.current = 0;
                    conflictRunStartedAtRef.current = 0;
                    dispatchAppEvent('seasuite:state-conflict');
                    announceFailed('conflict');
                  }
                } else {
                  conflictDeferralsRef.current = 0;
                  conflictRunStartedAtRef.current = 0;
                }

                if (anyMergedChange) {
                  // Carry this payload's receipt onto the merged snapshot, so
                  // the retry below announces success for the generation the
                  // user is actually waiting on rather than going unreported.
                  if (coveredGeneration !== undefined) {
                    snapshotGenerationsRef.current.set(merged, coveredGeneration);
                  }
                  stateRef.current = merged;
                  setState(merged);
                  pendingState.current = merged;
                  localDirtyRef.current = true;
                  // Retry immediately — unlike the debounce on a fresh user edit
                  // (which exists to coalesce further keystrokes), this payload is
                  // already a fully computed, stable merge with nothing left to
                  // coalesce. Waiting another SAVE_DEBOUNCE_MS here only widens the
                  // window during which a client-side navigation or reload can abort
                  // this already-resolved write before it ever leaves the browser —
                  // the record was safely merged in memory but never reached the
                  // server, which reads as silent data loss (#4845, #4896, #4897).
                  // Cancel any stale pending timer first so a slow prior schedule
                  // can't fire a duplicate write on top of this one.
                  if (saveTimer.current) {
                    clearTimeout(saveTimer.current);
                    saveTimer.current = null;
                  }
                  //
                  // The one exception is a run that has just crossed the
                  // deferral ceiling. Retrying immediately there means every
                  // refusal issues the next write on the following microtask,
                  // so a server that keeps refusing turns this into an
                  // unbounded write storm at network speed — the shape #5429's
                  // provocation produces, and one a fast enough external
                  // writer can produce for real. The user has now been told
                  // their edit has not landed, so the payload is handed back to
                  // the ordinary debounced save instead: still retried, still
                  // never dropped, but at a cadence a refusing server survives.
                  if (!inFlightSaveRef.current && !ceilingCrossed) {
                    const s = pendingState.current;
                    pendingState.current = null;
                    localDirtyRef.current = false;
                    persistState(s, 'conflict-retry').catch(() => {
                      localDirtyRef.current = true;
                    });
                  }
                  // else: a write is already in flight (e.g. a race with another
                  // resolution) — its own .finally() will pick up localDirtyRef /
                  // pendingState and schedule a follow-up, same as any other queued
                  // edit.
                } else {
                  // Nothing survives to replay (no user changes, or every changed
                  // key was a genuine/unresolvable conflict) — adopt fresh as-is.
                  if (saveTimer.current) {
                    clearTimeout(saveTimer.current);
                    saveTimer.current = null;
                  }
                  pendingState.current = null;
                  localDirtyRef.current = false;
                  stateRef.current = fresh;
                  setState(fresh);
                }

                // Tell the user exactly when their own change did not survive:
                // at least one key was reverted to the server's value, or the
                // resolution could not classify the edit at all. A 409 that
                // merged cleanly stays silent — that is a normal, recovered
                // event and warning on it would be noise (#5037).
                if (revertedKeys.length > 0 || !canClassify) {
                  logDiag('conflict.banner', {
                    source,
                    staleMsAtConflict,
                    externalChangedKeys,
                    userChangedKeys,
                    overlappingKeys,
                    revertedKeys,
                    unclassifiable: !canClassify,
                    overlapDetail,
                  });
                  reportConflictBanner('reverted-or-unclassifiable', {
                    source,
                    staleMsAtConflict,
                    overlappingKeys,
                    userChangedKeys,
                    revertedKeys,
                    unclassifiable: !canClassify,
                    overlapDetail,
                  });
                  dispatchAppEvent('seasuite:state-conflict');
                  // The banner is app-shell-wide; this receipt tells the
                  // specific surface the user acted on that its own change did
                  // not survive, so it cannot show a success confirmation
                  // alongside the banner (#5037, #5170).
                  announceFailed('conflict');
                } else if (!anyMergedChange) {
                  // Nothing was reverted, the resolution classified cleanly, and
                  // nothing of the user's survived to be replayed — which leaves
                  // exactly one case: `userChangedKeys` was empty, so this edit
                  // carried no top-level change against the pre-save server state
                  // and the server's copy already holds everything it asked for.
                  //
                  // This branch is TERMINAL: pendingState is cleared, localDirty
                  // is false, and no retry follows. Without a receipt here the
                  // generation the surface is waiting on is never answered either
                  // way, and useSaveStatus falls through to its 15s timeout and
                  // reports "Not confirmed — check your connection" over a write
                  // that is settled and a connection that is fine. The file's
                  // contract (see the save-receipt note in the header) is that
                  // EVERY terminal outcome announces itself; this was the one
                  // path that did not.
                  //
                  // Saved, not failed: the stored state matches the user's
                  // intent, which is the same reading the blob-owned-key
                  // shortcut above takes when it has nothing to write either.
                  announceSaved();
                }
              })
              .catch((reloadErr) => {
                console.error(
                  '[usePersistence] 409 reload failed:',
                  reloadErr?.message || reloadErr
                );
                // The edit is now stranded: pendingState/localDirty were
                // cleared before this write started, the write was refused,
                // and the reload that would have merged or reverted it never
                // arrived. Without re-queueing here the change lives only in
                // stateRef and is never written again — silent data loss with
                // no banner, which is exactly the failure the conflict
                // machinery exists to prevent. Treat it like any other
                // retryable failure: keep the payload, arm the backoff, and
                // let the write-error banner fire if it keeps failing.
                //
                // Same generation guard as the resolution path above: if a newer
                // local edit landed while this reload was failing, pendingState
                // already holds it (or its own timer already consumed it), and
                // overwriting that with this older snapshot would discard the
                // newer edit — the exact failure the race guard exists to stop.
                // stateRef was never reverted, so the newer edit carries this
                // one's changes forward too.
                if (localGenerationRef.current === startGeneration) {
                  pendingState.current = stateToSave;
                  localDirtyRef.current = true;
                }
                writeFailuresRef.current += 1;
                if (
                  writeFailuresRef.current >= WRITE_ERROR_BANNER_AFTER_FAILURES &&
                  !writeErrorShownRef.current
                ) {
                  writeErrorShownRef.current = true;
                  dispatchAppEvent('seasuite:write-error');
                  announceFailed('unreachable');
                }
                consumeReconnectSignal();
                if (!saveTimer.current && !inFlightSaveRef.current) {
                  saveTimer.current = setTimeout(() => {
                    saveTimer.current = null;
                    if (!pendingState.current || inFlightSaveRef.current) return;
                    const s = pendingState.current;
                    pendingState.current = null;
                    localDirtyRef.current = false;
                    persistState(s, 'conflict-reload-retry').catch(() => {
                      localDirtyRef.current = true;
                    });
                  }, retryDelayFor(writeFailuresRef.current));
                }
              });
            return;
          }
          if (e?.httpStatus === 401) {
            // Terminal auth failure: azureClient has already fired
            // 'seasuite:session-expired' and the session is being torn down.
            // Retrying is futile (the token is gone) and would spin a doomed
            // loop, so clear the retry flags. The unsaved edits remain in
            // stateRef; the session-expired listener below warns the user.
            pendingState.current = null;
            localDirtyRef.current = false;
            if (saveTimer.current) {
              clearTimeout(saveTimer.current);
              saveTimer.current = null;
            }
            // Terminal: no retry will follow, so this edit is definitively
            // unsaved. The session-expired alert below covers the workspace;
            // the receipt covers the surface the user acted on.
            announceFailed('session-expired');
            return;
          }
          if (e?.httpStatus === 422) {
            // GH #6271/#6270: a terminal, permanent refusal (e.g. the
            // employees-write EMP-B-002 evidence-immutability guard) — the
            // server has already said no on the merits, and retrying the
            // identical payload would be refused identically forever.
            // Clear the retry flags rather than feeding it into the
            // "retryable error" path below, which would silently keep
            // re-sending a doomed write and eventually show the generic
            // "check your connection" banner for a failure connectivity has
            // nothing to do with.
            pendingState.current = null;
            localDirtyRef.current = false;
            if (saveTimer.current) {
              clearTimeout(saveTimer.current);
              saveTimer.current = null;
            }
            announceFailed('refused');
            dispatchWriteErrorBanner(e?.message);
            return;
          }
          // Retryable error (network blip, 5xx, throttle): keep the edits and
          // keep retrying on a backing-off schedule until one lands, so an
          // outage longer than a single retry interval does not lose the
          // user's work. Each attempt that fails re-enters this branch and
          // schedules the next one, so the chain is only ever broken by a
          // success, a terminal error (401/409/422 handled above), or unmount.
          pendingState.current = stateToSave;
          localDirtyRef.current = true;
          writeFailuresRef.current += 1;
          // This write got no answer, so we do not know whether it stored
          // anything. Remember what it was carrying: if it did land, the retry
          // is refused and the resolution above needs to recognise these values
          // in the row as ours rather than another session's (GH #5191).
          unverifiedWriteRef.current = { payload: blobPayload };

          // Tell the user once the failures stop looking like a blip. Without
          // this the retry is completely silent, and an edit that never lands
          // (offline until the tab closes) is lost with no warning at all.
          if (
            writeFailuresRef.current >= WRITE_ERROR_BANNER_AFTER_FAILURES &&
            !writeErrorShownRef.current
          ) {
            writeErrorShownRef.current = true;
            dispatchAppEvent('seasuite:write-error');
            announceFailed('unreachable');
          }

          // This is the offline-recovery path (GH #5253). The write that just
          // failed was almost certainly the one issued while the connection was
          // down; if the browser has since told us the network is back, retry
          // near-immediately rather than waiting out a backoff that has already
          // climbed toward its 30s ceiling. A reload inside that window discards
          // the edit with nothing but the write-error banner to show for it.
          consumeReconnectSignal();

          if (!saveTimer.current) {
            saveTimer.current = setTimeout(() => {
              // Reset before the early-return guards so a later persistState call's
              // `if (!saveTimer.current)` check can schedule a fresh timer —
              // otherwise this stale (already-fired) ref permanently blocks every
              // future retry for the rest of the session.
              saveTimer.current = null;
              if (!pendingState.current || inFlightSaveRef.current) return;
              const s = pendingState.current;
              pendingState.current = null;
              localDirtyRef.current = false;
              persistState(s, 'network-retry');
            }, retryDelayFor(writeFailuresRef.current));
          }
        })
        .finally(() => {
          inFlightSaveRef.current = false;
          // If something was marked dirty while we were writing (e.g., an
          // autosave tick was skipped by the inFlight guard), schedule a
          // follow-up write so local changes are never silently dropped.
          if (localDirtyRef.current && pendingState.current && !saveTimer.current) {
            // GH #6522: the dirty edit may be an ordinary autosave tick (wait
            // out SAVE_DEBOUNCE_MS as usual), or it may be a terminal commit
            // that arrived while this write was in flight — see
            // commitAwaitingInFlightRef's declaration and flushPendingSave's
            // in-flight branch below. A commit asked for the write "now"; do
            // not silently downgrade it to the ordinary debounce.
            const commitNow = commitAwaitingInFlightRef.current;
            commitAwaitingInFlightRef.current = false;
            saveTimer.current = setTimeout(
              () => {
                // Reset before the early-return guards so a later persistState call's
                // `if (!saveTimer.current)` check can schedule a fresh timer —
                // otherwise this stale (already-fired) ref permanently blocks every
                // future retry for the rest of the session.
                saveTimer.current = null;
                if (!pendingState.current || inFlightSaveRef.current) return;
                const s = pendingState.current;
                pendingState.current = null;
                localDirtyRef.current = false;
                persistState(s, 'post-flight-retry').catch(() => {
                  localDirtyRef.current = true;
                });
              },
              commitNow ? 0 : SAVE_DEBOUNCE_MS
            );
          }
        });
    },
    [
      tenantId,
      reportNotPersisted,
      settleRewritten,
      reapplyAdoptedEmployees,
      adoptServerEmployee,
      reapplyAdoptedTasks,
      adoptServerTasks,
    ]
  );

  const flushPendingSave = useCallback(
    (opts = {}) => {
      // F-47: promote the latest in-memory edits into pendingState BEFORE
      // touching the debounce timer, so the write below always captures the
      // freshest snapshot even if nothing had ticked pendingState yet.
      if (
        !pendingState.current &&
        stateRef.current &&
        stateRef.current !== serverStateRef.current
      ) {
        pendingState.current = stateRef.current;
      }
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      // Never start a second concurrent write — the in-flight write's finally()
      // will schedule a retry if localDirtyRef is still true when it completes.
      //
      // GH #6522: a terminal commit (useCommitNow — Enable/Disable, a modal's
      // "Save and close", …) that lands here while another write is already in
      // flight is the same "commit defeated by timing" shape #5660 fixed for
      // the OTHER race (React not having applied the update yet). See
      // commitAwaitingInFlightRef's declaration above for why this uses a
      // dedicated ref rather than commitRequestedRef.
      if (inFlightSaveRef.current) {
        localDirtyRef.current = true;
        commitAwaitingInFlightRef.current = true;
        return Promise.resolve();
      }
      const stateToSave = pendingState.current && latestUnsavedSnapshot();
      if (!stateToSave || stateToSave === serverStateRef.current) {
        // Nothing to write *yet*. If React has an update in flight this is the
        // timing hole described at commitRequestedRef — the caller's record is
        // real, it just is not in stateRef until the render pass. Remember the
        // ask so the effect below issues it immediately rather than debouncing.
        if (localDirtyRef.current) commitRequestedRef.current = true;
        return Promise.resolve();
      }
      pendingState.current = null;
      localDirtyRef.current = false;
      return persistState(stateToSave, 'flush', opts);
    },
    [persistState, latestUnsavedSnapshot]
  );

  // Stable ref so the unmount-only effect always calls the current flush fn.
  const flushPendingSaveRef = useRef(flushPendingSave);
  useEffect(() => {
    flushPendingSaveRef.current = flushPendingSave;
  }, [flushPendingSave]);

  // Connectivity restored — retry immediately instead of waiting out the
  // remaining backoff. Every retry while offline is guaranteed to fail, so
  // the browser telling us the network is back is the best possible cue that
  // the next attempt can actually succeed.
  useEffect(() => {
    if (!tenantId || !stateReady) return;
    // `resetBudget` mirrors networkRetryQueue.js's own reconnect contract
    // (#5658): an 'online' event (or the navigator.onLine poll that stands in
    // for it) is positive evidence connectivity CHANGED, so it collapses the
    // backoff. Focus/visibility below say only that the user came back to the
    // tab — worth retrying on, but not evidence of anything having changed —
    // so they must NOT reset writeFailuresRef (that would let a genuinely dead
    // connection look freshly-failing forever and never reach the write-error
    // banner).
    const retryPendingWrite = (resetBudget) => {
      // A write is in flight. It is almost certainly the one issued while the
      // connection was down, so it is going to fail — but we cannot start a
      // second concurrent write (the one-in-flight invariant), and we cannot
      // see the payload from here either: persistState's callers null
      // pendingState the moment a write starts, so the `!pendingState.current`
      // check below would ALSO bail out. That combination is why the reconnect
      // used to be dropped entirely in the commonest offline-recovery timing.
      // Record it instead and let the failing write's own handler collapse the
      // backoff (GH #5253).
      if (inFlightSaveRef.current) {
        if (resetBudget) reconnectedWhileInFlightRef.current = true;
        return;
      }
      if (!pendingState.current) return;
      if (resetBudget) writeFailuresRef.current = 0;
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      flushPendingSaveRef.current();
    };
    const onOnline = () => retryPendingWrite(true);
    window.addEventListener('online', onOnline);

    // Poll navigator.onLine directly as well — see ONLINE_POLL_MS. Only the
    // offline -> online transition should trigger a retry; polling while
    // already online would ignore the backoff and hammer a server that is
    // simply slow, so track the last observed state across ticks.
    let wasOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
    const pollTimer = setInterval(() => {
      let offlineNow = false;
      try {
        offlineNow = typeof navigator !== 'undefined' && navigator.onLine === false;
      } catch {
        offlineNow = false;
      }
      if (wasOffline && !offlineNow) onOnline();
      wasOffline = offlineNow;
    }, ONLINE_POLL_MS);

    // Reconnect signals beyond 'online' (#5356, and the same gap
    // networkRetryQueue.js closed for the per-record stores): a tab a user
    // switched away from mid-outage can regain connectivity with no 'online'
    // event ever firing in it (some browsers only deliver it to the
    // foreground tab, and it is not delivered at all by some network-
    // emulation/automation layers), and the moment the user returns to look
    // at it is also the moment they are most likely to close it — losing a
    // pending write that a plain reconnect retry would otherwise have landed.
    // Focus/tab-visible are independent, always-delivered evidence worth
    // retrying on even though they prove nothing about the network itself.
    const onFocus = () => retryPendingWrite(false);
    window.addEventListener('focus', onFocus);
    const onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        retryPendingWrite(false);
      }
    };
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onFocus);
      if (typeof document !== 'undefined' && document.removeEventListener) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      clearInterval(pollTimer);
    };
  }, [tenantId, stateReady]);

  useEffect(() => {
    // The cleanup below clears whatever timer is armed — including a retry
    // armed by a failed save, which is stored in the same ref. If we bail out
    // here without arming a replacement while a failed write is still pending,
    // that retry chain is broken for good and the edit is silently lost. So
    // when we early-return, hand the pending work back to flushPendingSave
    // (which re-arms) rather than just dropping through.
    const hasPendingFailedWrite = () => !!pendingState.current && localDirtyRef.current;
    if (!tenantId || !stateReady || state === serverStateRef.current) {
      // Nothing of the user's is waiting on the debounce — a failed write's own
      // backoff below owns its retry, and the ceiling must not carry an expired
      // budget into the next run of edits.
      if (!hasPendingFailedWrite()) oldestPendingEditAtRef.current = 0;
      if (hasPendingFailedWrite() && !saveTimer.current && !inFlightSaveRef.current) {
        saveTimer.current = setTimeout(() => {
          saveTimer.current = null;
          if (!pendingState.current || inFlightSaveRef.current) return;
          const s = pendingState.current;
          pendingState.current = null;
          localDirtyRef.current = false;
          persistState(s, 'orphan-retry');
        }, retryDelayFor(writeFailuresRef.current));
      }
      return;
    }
    pendingState.current = state;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // Start the durability clock on the FIRST edit of a run, and leave it
    // running across every re-arm, so continuous typing cannot postpone the
    // write past MAX_SAVE_DEFER_MS. A ref, not a local: this effect re-runs on
    // every state change, so anything scoped to one run is reset by the very
    // keystrokes the ceiling exists to bound.
    if (!oldestPendingEditAtRef.current) oldestPendingEditAtRef.current = Date.now();
    // A terminal commit that arrived before React applied its own update — see
    // commitRequestedRef. The caller has already declared there is nothing left
    // to coalesce, so write on this tick instead of waiting the debounce out.
    // Consumed here, so one commit buys one immediate write and later ordinary
    // edits debounce normally again.
    const commitNow = commitRequestedRef.current;
    commitRequestedRef.current = false;
    const debounceMs = commitNow
      ? 0
      : Math.max(
          0,
          Math.min(
            SAVE_DEBOUNCE_MS,
            oldestPendingEditAtRef.current + MAX_SAVE_DEFER_MS - Date.now()
          )
        );
    // When the debounce first started waiting on an external write, so the
    // deferral below can be bounded rather than open-ended.
    let deferringSince = 0;
    const fireDebouncedSave = () => {
      // Reset before the early-return guards so persistState's own internal
      // `if (!saveTimer.current)` retry check isn't blocked by this timer's own
      // (now-fired, stale) id — otherwise the very first debounced save, if it
      // fails, can never get a retry scheduled at all.
      saveTimer.current = null;
      if (!pendingState.current) return;
      if (inFlightSaveRef.current) {
        // A write is already in flight — mark dirty so the retry fires after it
        // settles (see the finally() block in persistState).
        localDirtyRef.current = true;
        // Restart the durability clock: pendingState was emptied when that
        // write started, so whatever is queued now was typed after it and is
        // owed its own window — not a budget that has already run down. Without
        // this the ceiling stays expired and every later keystroke arms a
        // zero-delay timer that can only land back here.
        oldestPendingEditAtRef.current = 0;
        return;
      }
      if (externalStateWritesRef.current > 0) {
        // A document save or workflow action that will bump tenant_state's row
        // version is still in flight. It reports the new version back through
        // adoptStateVersion when it returns; writing before then would send the
        // superseded one and be refused. That refusal is indistinguishable from
        // a real concurrent writer, so it surfaces as "Workspace updated
        // elsewhere" to a user who is alone in the tenant (GH #5314). Wait for
        // it instead of racing it.
        const now = Date.now();
        if (!deferringSince) deferringSince = now;
        if (now - deferringSince < MAX_EXTERNAL_WRITE_DEFER_MS) {
          localDirtyRef.current = true;
          saveTimer.current = setTimeout(fireDebouncedSave, EXTERNAL_WRITE_RECHECK_MS);
          return;
        }
        // Deferred too long — the release was missed somewhere. Fall through
        // and write anyway: a refused write is recoverable, an unwritten edit
        // is not.
        logDiag('persist.external-write-defer-timeout', {
          waitedMs: now - deferringSince,
          pending: externalStateWritesRef.current,
        });
      }
      deferringSince = 0;
      const stateToSave = latestUnsavedSnapshot();
      pendingState.current = null;
      localDirtyRef.current = false;
      // This snapshot is on its way to the server, so the run it belongs to is
      // over and the next edit starts a fresh window.
      oldestPendingEditAtRef.current = 0;
      persistState(stateToSave, 'timer').catch(() => {
        localDirtyRef.current = true;
      });
    };
    saveTimer.current = setTimeout(fireDebouncedSave, debounceMs);
    // Only cancel the timer on re-run — do NOT flush here.  A flush on every
    // state change would race with the debounce write above (both use the same
    // stateVersionRef → 409).  The unmount-only effect below handles flushing
    // when the component actually unmounts (SPA navigation, tab close).
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
    };
  }, [state, tenantId, stateReady, persistState, latestUnsavedSnapshot]);

  // Flush on component unmount (SPA navigation / route change).  Kept separate
  // from the state-watching effect so it fires exactly once — on actual unmount
  // — rather than on every state update.
  //
  // F-47: fast navigation (unmount immediately followed by a real page
  // unload/redirect, e.g. logout or a tenant switch) can cut a plain fetch off
  // mid-flight. Request keepalive delivery so the browser guarantees the write
  // completes even if the page starts tearing down right after — same
  // technique as the beforeunload/pagehide handler below. navigator.sendBeacon
  // isn't usable here: it can't set the Authorization header this endpoint
  // requires, so keepalive fetch is the closest guaranteed-delivery mechanism
  // that still authenticates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    () => () => {
      // GH #6306: a page whose state was never loaded from the server has
      // nothing legitimate to save — what sits in stateRef is the seed state.
      // That is the employee/candidate portals, the magic-link verify screens
      // and the public vendor page: App.jsx calls useAppState above the
      // `/portal` early return and hooks cannot be conditional, so they all
      // mount this layer and used to issue an unauthenticated state-write on
      // every navigation, which state-write answered 401, invisibly.
      //
      // Keyed on serverStateRef rather than on the session for the reason set
      // out at the teardown handler below: a session that has GONE (expiry, or
      // a sibling tab logging out) still has real loaded state worth saving.
      // This path has no local backstop, so the write is simply attempted.
      if (!serverStateRef.current) return;
      flushPendingSaveRef.current({ keepalive: true }).catch(() => {});
    },
    []
  );

  useEffect(() => {
    // Best-effort final save on tab close. The browser does not await async work
    // in beforeunload, so flushPendingSave()'s normal fetch can be killed mid-
    // flight and the last edit lost. Use a keepalive write instead — the browser
    // guarantees delivery of a keepalive request even as the page unloads. The
    // versioned write keeps the optimistic-concurrency contract (DB-CONC-001)
    // intact: if our snapshot is stale the server rejects it rather than clobber.

    // The snapshot the teardown write has already been issued for. Both
    // 'beforeunload' and 'pagehide' fire on an ordinary navigation and both are
    // bound to this same handler, so without this marker the identical payload
    // is sent twice carrying the identical stateVersionRef — and the second one
    // is a guaranteed self-inflicted 409 (GH #5251). No data is lost (the first
    // write lands and the second is swallowed below), but it is a genuinely
    // refused write: it shows up in every API trace, is indistinguishable from
    // a real concurrent-writer conflict, and inflates the conflict rate the
    // storage layer is judged on.
    //
    // serverStateRef is deliberately NOT used as the marker. This write is
    // unacknowledged at the point we record it, and treating it as server state
    // would feed the 409 classifier a baseline the server may never have
    // stored — the "a write was attempted" / "a write landed" conflation
    // STG-B-003 exists to prevent. Reference identity is the right grain:
    // updateState() produces a fresh object for every edit, so a genuine
    // later change (e.g. after a bfcache restore) is a different snapshot and
    // is still written.
    let unloadWrittenSnapshot = null;

    const handleBeforeUnload = () => {
      // GH #6306 — FIRST, before anything reads state or touches the backup key.
      //
      // This handler runs on every page in the app, including the ones with no
      // user session: the employee and candidate portals, the magic-link verify
      // screens, the public vendor self-assessment page. On those, stateRef
      // holds the seed state (the load effect refuses to run without a session),
      // so the network write went out unauthenticated and came back 401.
      //
      // The 401 was the harmless half. writeTeardownBackup() below keyed its
      // backup on the TENANT alone (it is user-scoped since GH #6307, but this
      // guard is still what stops a session-less page writing at all), and the
      // portal shares an origin and a tenant with the app — so a session-less
      // page overwrote the backup holding a real user's unsaved edit with seed
      // state under a null version, and the next boot's replay guard then
      // discarded it as version-mismatched. That
      // is precisely the loss GH #6284 added the backup to prevent, arriving
      // through a different door. An employee opening their own magic link in
      // the same browser was enough to trigger it.
      //
      // The predicate is "was server state ever loaded", NOT "is there a
      // session". Those name different states, and the difference is the whole
      // fix. A page that NEVER had a session (the portal) holds seed state and
      // must write nothing. A page that no longer has one — a session that
      // expired mid-edit, or a sibling tab logging out, which nulls `session`
      // via useAuth's storage listener without even firing the session-expired
      // alert — holds REAL loaded state, dirty, under a still-current row
      // version. That is precisely the case this backup exists for, so keying
      // the guard on the session would delete the recovery path in the one
      // moment it matters. `serverStateRef` is null if and only if the load
      // effect never completed, and on a session-less surface it is guaranteed
      // null because every one of its setters is session-guarded.
      //
      // Backups already written by a session-less page before this guard landed
      // carry a null version, so replayOrphanedTeardownBackup discards them on
      // the next boot; no server state was ever affected and none needs repair.
      if (!serverStateRef.current) return;
      // An in-flight write is NOT a reason to skip this one. A plain fetch has
      // no delivery guarantee across a navigation — the browser cancels it
      // (net::ERR_ABORTED) — so bailing out here because "a versioned write is
      // already going" was betting the user's edit on a request the unload was
      // about to kill, and losing that bet silently (GH #5508, #5509, #5510).
      // Issuing the keepalive write anyway is the safe side of that trade: it
      // carries the same stateVersion, so exactly one of the two can land.
      // If the in-flight write got through, this one is refused with a 409 and
      // swallowed below; if it was cancelled, this one is the only copy of the
      // edit that survives. STG-B-003's principle applies to durability the
      // same way it applies to confirmations — a refused write is recoverable,
      // an unwritten edit is not.
      // GH #7761: the newest snapshot, never a queued one React has since
      // superseded — this write carries the version as of NOW.
      const stateToSave = latestUnsavedSnapshot();
      if (!stateToSave || stateToSave === serverStateRef.current) return;
      if (stateToSave === unloadWrittenSnapshot) return; // already sent on the sibling event
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
      }
      try {
        unloadWrittenSnapshot = stateToSave;
        const blobPayload = stripBlobOwnedKeys(stateToSave);
        const writeVersion = stateVersionRef.current;
        const adoptSeqAtTeardown = adoptSeqRef.current;
        // GH #6158/#6149/#6012: back this up to localStorage BEFORE issuing
        // the network write. localStorage.setItem is synchronous and commits
        // before this handler returns, so it survives even when the keepalive
        // write is quota-rejected or the plain-fetch fallback is aborted mid-
        // flight by the navigation it was racing — both silent failures with
        // no banner and nothing left to retry. Cleared on confirmed delivery
        // below; replayed on next boot by replayOrphanedTeardownBackup if it
        // never was.
        // GH #6307: keyed on the tenant AND the principal the loaded state
        // belongs to. A tenant-only key made this backup replayable by the next
        // person to log into the same tenant on the same browser.
        // GH #6607: record WHAT the user changed, and what the row held when
        // they changed it, alongside the payload.
        //
        // Without those two, the next boot's replay can only compare the
        // backup's version against the row's current version, and a mismatch
        // tells it nothing about whose change moved the row. It used to
        // resolve that ambiguity by deleting the backup — so an unrelated
        // writer (a sweeper, a document workflow, another tab, another module)
        // bumping the row between the teardown and the next boot silently
        // discarded the user's unsaved edit without ever asking the server.
        // The server's contract is the opposite: send the write, and a 409
        // hands back the state that won so the client can merge. These two
        // fields are what let the replay do that (see
        // replayOrphanedTeardownBackup).
        const baselineState = serverStateRef.current;
        const changedKeys = getChangedTopLevelKeys(stripBlobOwnedKeys(baselineState), blobPayload);
        const baseline = {};
        for (const k of changedKeys) baseline[k] = baselineState?.[k];
        // CONC-01: the edit as field-level operations, when there is a
        // baseline to diff against. This is what the unload write sends and
        // what the next boot replays; payload/version stay for a backend that
        // does not take patches.
        const ops =
          baselineState && patchSupportedRef.current
            ? diffState(stripBlobOwnedKeys(baselineState), blobPayload)
            : null;
        const stored = writeTeardownBackup(tenantId, backupOwnerRef.current, {
          payload: blobPayload,
          version: writeVersion,
          ts: Date.now(),
          changedKeys,
          baseline,
          ...(ops ? { ops } : {}),
        });
        // The classification fields are the only part of this backup that can
        // push it over the storage quota. Losing the backup entirely is a
        // strictly worse outcome than losing the ability to merge it, so fall
        // back to the plain shape rather than storing nothing.
        if (!stored) {
          const fallbackStored = writeTeardownBackup(tenantId, backupOwnerRef.current, {
            payload: blobPayload,
            version: writeVersion,
            ts: Date.now(),
            ...(ops ? { ops } : {}),
          });
          // A tenant losing edits to a storage quota must leave a trace —
          // silently storing nothing is how this stops being findable at all.
          // logDiag alone does not deliver that: diagnostics is a per-tenant,
          // time-boxed opt-in that is off by default, and the moment the quota
          // bites is precisely the moment nobody has turned it on. The console
          // line is the one that is always there; logDiag carries the detail
          // when a tenant is being watched.
          console.warn(
            '[usePersistence] Teardown backup could not be stored in full — ' +
              'an unsaved edit may not be recoverable on the next load.'
          );
          logDiag('teardown.backup-degraded', {
            fallbackStored,
            changedKeys,
            approxPayloadBytes: approxBytes(blobPayload),
          });
          if (!fallbackStored) {
            // Neither shape fit. An OLDER backup may still be sitting under this
            // key, taken against a different edit session — and since GH #6607 a
            // stale backup is MERGED rather than discarded, so leaving it would
            // resolve this session's absent edit against someone else's
            // baseline. Remove it rather than leave a wrong one behind.
            clearTeardownBackup(tenantId, backupOwnerRef.current);
          }
        }
        // The backup above is taken unconditionally; only the doomed network
        // call is skipped. Once the session is gone the write can only 401, and
        // the backup IS the recovery path for that exact case — dropping it
        // here would lose the edit that the next boot could otherwise replay.
        const writeWholeBlob = () => {
          saveTenantStateVersioned(tenantId, blobPayload, writeVersion, {
            keepalive: true,
            // Declared here too, so a stray store-owned key on the unload write
            // is refused rather than dropped. No onNotPersisted: this page is
            // being torn down and has nowhere to show a banner — the teardown
            // backup in localStorage is what recovers this path.
            blobKeysOwnedByStores: BLOB_KEYS_OWNED_BY_STORES,
          })
            .then((v) => {
              // pagehide fires on bfcache / mobile tab-switch where the page can
              // come back alive — without adopting the returned version here,
              // the next save on this same page instance sends a stale version
              // and self-conflicts (see persistState's version-adoption comment).
              if (v != null) stateVersionRef.current = v;
              clearTeardownBackup(tenantId, backupOwnerRef.current);
            })
            .catch(() => {
              // Left in localStorage on purpose — this is the case the backup
              // exists for (delivery genuinely failed or was never confirmed).
            });
        };
        if (sessionRef.current && ops && !ops.length) {
          // Nothing of the blob changed since the last confirmed save.
          clearTeardownBackup(tenantId, backupOwnerRef.current);
        } else if (sessionRef.current && ops) {
          saveTenantStatePatch(tenantId, ops, { keepalive: true })
            .then((v) => {
              if (v != null) stateVersionRef.current = v;
              serverStateRef.current = stateToSave;
              // bfcache: the page may live on — an employee adopted while this
              // write was in flight must not fall back to its pre-adoption copy.
              reapplyAdoptedEmployees(adoptSeqAtTeardown);
              reapplyAdoptedTasks(adoptSeqAtTeardown);
              clearTeardownBackup(tenantId, backupOwnerRef.current);
            })
            .catch((e) => {
              // Left in localStorage: the next boot replays these operations.
              // A backend from before CONC-01 gets the whole-blob write, if
              // the page is still alive to send it (bfcache / tab switch).
              if (e?.httpStatus === 400 && /missing state/i.test(String(e?.message || ''))) {
                patchSupportedRef.current = false;
                writeWholeBlob();
              }
            });
        } else if (sessionRef.current) {
          writeWholeBlob();
        }
        pendingState.current = null;
        localDirtyRef.current = false;
      } catch {
        /* best-effort — nothing more we can do during unload */
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    // pagehide covers the bfcache / mobile tab-switch close where beforeunload
    // does not always fire.
    window.addEventListener('pagehide', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, [tenantId, latestUnsavedSnapshot]);

  // Warn the user if the session is torn down (401) while edits are unsaved.
  // azureClient fires 'seasuite:session-expired' on any 401; if a save was in
  // flight or queued at that moment the token is already gone, so the edits
  // cannot be persisted — tell the user instead of losing the work silently.
  useEffect(() => {
    const onExpired = () => {
      const hasUnsaved =
        inFlightSaveRef.current ||
        localDirtyRef.current ||
        !!pendingState.current ||
        (!!stateRef.current && stateRef.current !== serverStateRef.current);
      if (!hasUnsaved) return;
      try {
        window.alert(
          'Your session expired before your latest changes could be saved. ' +
            'Please sign in again and re-apply any unsaved edits.'
        );
      } catch {
        /* alert unavailable — non-blocking */
      }
    };
    window.addEventListener('seasuite:session-expired', onExpired);
    return () => window.removeEventListener('seasuite:session-expired', onExpired);
  }, []);

  // CONC-01 — "keep mine" on a same-field clash. The user has seen both
  // values and chosen their own: send exactly that one operation, marked as a
  // deliberate overwrite (the server logs it), against the state that won.
  // Only offered where describeConflict judged it safe. If the field moved yet
  // again, the user is shown the new clash rather than overwriting blind.
  useEffect(() => {
    if (!tenantId) return undefined;
    const valueAtPath = (st, path) => {
      let cur = st;
      for (const seg of path) {
        if (cur == null) return undefined;
        cur =
          typeof seg === 'string'
            ? cur[seg]
            : Array.isArray(cur)
              ? cur.find((r) => r && r[seg.k] === seg.v)
              : undefined;
      }
      return cur;
    };
    const onKeepMine = (ev) => {
      const conflict = ev?.detail?.conflict;
      if (!conflict || !conflict.overridable) return;
      const base = serverStateRef.current || stateRef.current;
      const op = keepMineOp(conflict, base);
      if (!op) return;
      if (conflict.employeeRecord) {
        // RDRC-04A: employees are not in the blob — they are saved per record
        // through employees-write, against the version this tab adopted with
        // the committed record. The user has seen both values and chosen
        // theirs-over-mine; their value goes on screen and the ordinary save
        // cycle sends it, naming the field so the server audits the overwrite.
        // If the record moved again, that save gets a 409.
        const { override: _override, ...plain } = op;
        // The field changed on screen since the choice was offered: nothing is
        // applied, and the clash is not reported as resolved.
        // The clash is overtaken, so it is withdrawn from the notice rather
        // than left up offering a choice that can no longer be applied.
        if (applyPatch(stateRef.current || {}, [plain]).conflicts.length) {
          fieldConflictsRef.current = fieldConflictsRef.current.filter((c) => c !== conflict);
          dispatchAppEvent('seasuite:state-conflict-withdrawn', { conflict });
          dispatchWriteErrorBanner(
            'your choice to keep your own value was not applied — the field has changed since. ' +
              'The value now shown stands.'
          );
          return;
        }
        updateStateRef.current?.((draft) => {
          draft.employees = applyKeepMine(draft, plain).employees;
        });
        const keyed = conflict.path[1];
        if (keyed && typeof keyed === 'object' && keyed.v != null) {
          employeeOverridesRef.current = [
            ...employeeOverridesRef.current,
            { employeeId: String(keyed.v), path: conflict.path.slice(2) },
          ];
        }
        fieldConflictsRef.current = fieldConflictsRef.current.filter((c) => c !== conflict);
        dispatchAppEvent('seasuite:state-conflict-resolved', { conflict });
        return;
      }
      saveTenantStatePatch(tenantId, [op])
        .then((v) => {
          serverStateRef.current = applyKeepMine(serverStateRef.current || {}, op);
          const next = applyKeepMine(stateRef.current || {}, op);
          stateRef.current = next;
          setState(next);
          if (v != null) stateVersionRef.current = v;
          fieldConflictsRef.current = fieldConflictsRef.current.filter((c) => c !== conflict);
          dispatchAppEvent('seasuite:state-conflict-resolved', { conflict });
          if (op.op !== 'insert') return undefined;
          // A restore is a create to every server guard, and some keep their
          // own copy of a created record (server-owned keys, externalAudits…)
          // behind a 200. Read back what was stored, and say so if the record
          // did not come back as sent — never a silent half-restore.
          return loadTenantStateWithVersion(tenantId).then(({ state: st, stateVersion: sv }) => {
            const fresh = migrateState(st);
            const stored = (valueAtPath(fresh, op.path) || []).find(
              (r) => r && r[op.idKey] === op.record[op.idKey]
            );
            const intact =
              !!stored &&
              Object.keys(op.record).every(
                (f) => JSON.stringify(stored[f]) === JSON.stringify(op.record[f])
              );
            if (intact) return;
            serverStateRef.current = fresh;
            stateRef.current = fresh;
            setState(fresh);
            if (sv != null) stateVersionRef.current = sv;
            dispatchWriteErrorBanner(
              'the deleted record could not be restored as you had it — Tryggr kept its own copy. ' +
                'Check it before re-entering anything.'
            );
          });
        })
        .catch((e) => {
          if (e?.httpStatus === 409 && Array.isArray(e.conflicts) && e.conflicts.length) {
            const fresh = e.serverState != null ? migrateState(e.serverState) : base;
            if (e.serverState != null) serverStateRef.current = fresh;
            const notice = collapseGone(e.conflicts, stateRef.current, base).map((c) =>
              describeConflict(c, fresh)
            );
            fieldConflictsRef.current = notice;
            dispatchAppEvent('seasuite:state-conflict', { conflicts: notice });
            return;
          }
          dispatchWriteErrorBanner(
            'your choice to keep your own value could not be saved — try again.'
          );
        });
    };
    window.addEventListener('seasuite:conflict-keep-mine', onKeepMine);
    return () => window.removeEventListener('seasuite:conflict-keep-mine', onKeepMine);
  }, [tenantId]);

  useEffect(() => {
    if (!tenantId || !session || !stateReady) return;

    let refreshRetries = 0;
    let refreshRetryTimer = null;
    let staleAnnounced = false;

    const clearRefreshRetry = () => {
      if (refreshRetryTimer) {
        clearTimeout(refreshRetryTimer);
        refreshRetryTimer = null;
      }
    };

    const refresh = (source, { force = false } = {}) => {
      const flags = {
        localDirty: localDirtyRef.current,
        pending: !!pendingState.current,
        inFlight: inFlightSaveRef.current,
        isRefreshing: isRefreshingRef.current,
        riskOutbox: hasPendingRiskWrites(),
        recordWrites: hasPendingRecordWrites(),
      };
      const elapsed = Date.now() - lastRefreshRef.current;
      // A backoff retry (#6393) sets its own interval and must not be swallowed
      // by the throttle meant for poll/visibility/realtime bursts.
      if (!force && elapsed < REFRESH_THROTTLE_MS) {
        logDiag('refresh.skip', { source, reason: 'throttle', flags });
        return;
      }
      // The blob flags below only know about the blob save. Every collection
      // that moved to a per-record store writes outside it — risks through the
      // outbox (ADR-0001), findings/soaData/supplier through their own
      // debounced calls — so after the blob save settles there is a window in
      // which those flags all read false while the user's edit is still on its
      // way to the server. A refresh landing in that window replaced `state`
      // with the server's copy and the edit vanished from the screen (#5174,
      // #5241). hasPendingRecordWrites() closes that window for the paths the
      // risk outbox check does not cover.
      if (
        localDirtyRef.current ||
        pendingState.current ||
        inFlightSaveRef.current ||
        isRefreshingRef.current ||
        hasPendingRiskWrites() ||
        hasPendingRecordWrites()
      ) {
        const reason = localDirtyRef.current
          ? 'localDirty'
          : pendingState.current
            ? 'pending'
            : inFlightSaveRef.current
              ? 'inFlight'
              : isRefreshingRef.current
                ? 'isRefreshing'
                : hasPendingRiskWrites()
                  ? 'riskOutbox'
                  : 'recordWrites';
        logDiag('refresh.skip', { source, reason, flags });
        return;
      }

      logDiag('refresh.start', { source, flags });
      lastRefreshRef.current = Date.now();
      isRefreshingRef.current = true;
      loadTenantStateWithVersion(tenantId)
        .then(({ state: loaded, stateVersion }) => {
          // The guard above is checked before this fetch starts, not after —
          // a local edit (e.g. updateState from a save) can begin while the
          // request is in flight. Applying a fetch that raced an edit would
          // silently discard it (the edit lives only in `stateRef`/pending
          // save state, not in this response). Re-check the same condition
          // now and skip applying a stale snapshot; the edit's own debounced
          // save carries it forward, and the next poll/visibility/realtime
          // signal will refresh normally once the user is no longer mid-edit.
          if (
            localDirtyRef.current ||
            pendingState.current ||
            inFlightSaveRef.current ||
            hasPendingRiskWrites() ||
            hasPendingRecordWrites()
          ) {
            logDiag('refresh.skip', { source, reason: 'raced-local-edit' });
            return;
          }
          const migrated = migrateState(loaded);
          serverStateRef.current = migrated;
          stateRef.current = migrated;
          stateVersionRef.current = stateVersion;
          setState(migrated);
          lastRefreshSuccessRef.current = Date.now();
          refreshRetries = 0;
          clearRefreshRetry();
          if (staleAnnounced) {
            staleAnnounced = false;
            dispatchAppEvent('seasuite:state-fresh');
          }
          logDiag('refresh.apply', { loadedBytes: approxBytes(migrated) });
        })
        .catch((e) => {
          console.error('[usePersistence] background refresh failed:', e.message);
          logDiag('refresh.skip', {
            source,
            reason: 'error',
            errorMessage: String(e?.message || e).slice(0, 200),
          });
          // #6393: retry quietly, then say so. The banner is raised only when
          // every backoff step has failed — at that point the app is knowingly
          // showing a stale copy and the user has to be told, because they are
          // editing against it.
          const backoff = REFRESH_RETRY_BACKOFF_MS[refreshRetries];
          refreshRetries += 1;
          if (backoff !== undefined) {
            clearRefreshRetry();
            refreshRetryTimer = setTimeout(() => {
              refreshRetryTimer = null;
              refresh('retry', { force: true });
            }, backoff);
            return;
          }
          if (!staleAnnounced) {
            staleAnnounced = true;
            dispatchAppEvent('seasuite:state-stale', {
              errorMessage: String(e?.message || e).slice(0, 200),
            });
          }
        })
        .finally(() => {
          isRefreshingRef.current = false;
        });
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh('visibility');
    };

    document.addEventListener('visibilitychange', handleVisibility);
    const poll = setInterval(() => refresh('poll'), REFRESH_INTERVAL_MS);

    const handleRealtimeSignal = () => refresh('realtime');
    window.addEventListener('seasuite:realtime-refresh', handleRealtimeSignal);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(poll);
      clearRefreshRetry();
      window.removeEventListener('seasuite:realtime-refresh', handleRealtimeSignal);
    };
  }, [tenantId, session, stateReady]);

  // Realtime refresh via Azure Web PubSub. Connects once state is ready and
  // calls the same throttled refresh as the polling path when a signal arrives.
  // Falls back silently to polling-only when the socket is unavailable.
  useEffect(() => {
    if (!tenantId || !session || !stateReady) return;

    // The negotiate JWT is read live from azureClient inside the client —
    // `session` here is the profile object, not a token.
    const client = createRealtimeClient({
      tenantId,
      onMessage: (signal) => {
        if (signal?.type === 'state-changed') {
          // Reuse the refresh logic defined in the sibling effect above.
          // The throttle guard prevents stampeding when multiple writers
          // save in quick succession.
          window.dispatchEvent(new CustomEvent('seasuite:realtime-refresh'));
          // Screens with their own narrower read (the risk register) hear the
          // same signal through realtimeSignals — one socket, not one each.
          emitRealtimeSignal(signal);
        }
      },
      // Screens that fall back to polling while no socket is live read this.
      onStatus: setRealtimeStatus,
    });

    return () => {
      client.disconnect();
      setRealtimeStatus('disconnected');
    };
  }, [tenantId, session, stateReady]);

  // Lets other write paths (document saves, workflow actions) that bump
  // tenant_state.state_version outside the normal debounced full-state save
  // report the new version back, so the next debounced save doesn't send a
  // stale version and self-conflict. null/undefined is a no-op (the caller's
  // write didn't touch tenant_state).
  const adoptStateVersion = useCallback((newVersion) => {
    if (newVersion != null) stateVersionRef.current = newVersion;
  }, []);

  // Claim the state version for the duration of a write issued by another path
  // that may bump it (a document save, a workflow action). Returns the release
  // function, which the caller MUST invoke in a finally — after its
  // adoptStateVersion call, so the debounced save resumes with the version the
  // server actually produced.
  //
  //   const release = beginExternalStateWrite();
  //   try {
  //     const result = await saveDocumentPatch(...);
  //     adoptStateVersion(result?.stateVersion ?? null);
  //   } finally {
  //     release();
  //   }
  //
  // adoptStateVersion alone is not enough: it can only report the new version
  // once the call returns, and the 1.5s debounce routinely fires before that.
  // Releasing twice is a no-op; a release that never happens is bounded by
  // MAX_EXTERNAL_WRITE_DEFER_MS so a missed one degrades to today's behaviour
  // rather than stalling the save.
  const beginExternalStateWrite = useCallback(() => {
    externalStateWritesRef.current += 1;
    let released = false;
    return () => {
      if (released) return;
      released = true;
      externalStateWritesRef.current = Math.max(0, externalStateWritesRef.current - 1);
    };
  }, []);

  // Hand both halves to the transport layer, so an endpoint that commits
  // tenant_state as a side effect of a user action is recognised there instead
  // of at each call site. The two call sites that caused this — the assign and
  // share modals — reach documents-assign straight through azureFetch and have
  // no access to these hooks at all, so the version they moved themselves was
  // never adopted and their user's next autosave was refused against their own
  // write. See src/api/tenantStateVersion.js.
  useEffect(
    () =>
      registerTenantStateVersionHooks({
        adoptStateVersion,
        beginExternalStateWrite,
        // GH #7714: the version this tab holds, so a writer's version is
        // adopted only when the writer read that same version.
        getStateVersion: () => stateVersionRef.current ?? null,
      }),
    [adoptStateVersion, beginExternalStateWrite]
  );

  // Returns the edit generation this call produced. Pass it to
  // useSaveStatus().track() to learn whether the write carrying it actually
  // landed; ignore it (as almost every caller does) for the previous
  // fire-and-forget behaviour. See the save-receipt note in the header.
  //
  // GH #7761: the mutator runs HERE, at call time, and stateRef holds the
  // result before this returns. It used to run inside a setState updater,
  // which React calls during its render pass — so between this call and that
  // render, stateRef still held the previous snapshot. A writer endpoint
  // (obligations-enable) has its version adopted by the transport and its
  // result applied by the caller in one uninterrupted microtask chain; with a
  // lazy mutator the result was not in stateRef yet, and a page teardown or
  // debounced save in that window sent the PRE-enable snapshot under the
  // POST-enable version. state-write accepted it and read the missing entry
  // as a disable: a confirmed enablement undone in silence.
  //
  // Every write path reads stateRef (latestUnsavedSnapshot below), so the
  // version and the content it describes can no longer come apart. Every
  // internal setState call already assigns stateRef first, so stateRef is
  // always the newest state and building on it equals building on `prev`.
  // Running the mutator once, outside React, also keeps it once-only under
  // StrictMode — the reason it lived in the updater.
  //
  // Re-entrancy: an updateState called from INSIDE a mutator applies to the
  // outer draft. Copying stateRef there would build on the pre-outer state,
  // and the outer call's assignment would then drop the inner edit in silence
  // (React's queued updater used to keep both).
  const activeDraftRef = useRef(null);
  const updateState = useCallback((fn) => {
    if (activeDraftRef.current) {
      fn(activeDraftRef.current);
      return localGenerationRef.current;
    }
    const dirtyBefore = localDirtyRef.current;
    localDirtyRef.current = true;
    localGenerationRef.current += 1;
    const generation = localGenerationRef.current;
    const next = JSON.parse(JSON.stringify(stateRef.current));
    activeDraftRef.current = next;
    try {
      fn(next);
    } finally {
      activeDraftRef.current = null;
    }
    stateRef.current = next;
    // Tag the snapshot itself rather than relying on the counter, which has
    // already moved on by the time a write picks this payload up.
    snapshotGenerationsRef.current.set(next, generation);
    logDiag('state.update', {
      label: fn?.name || null,
      dirtyBefore,
      stateBytes: approxBytes(next),
    });
    setState(next);
    return generation;
  }, []);
  updateStateRef.current = updateState;

  // Whole-state replacement for callers outside this hook. Every write path
  // reads stateRef (latestUnsavedSnapshot), so a replacement that bypassed it
  // would let a save carry content the screen no longer shows (GH #7761). The
  // functional form is refused: its updater would run at render time, which
  // is exactly the gap this file closed.
  const replaceState = useCallback((next) => {
    if (typeof next === 'function') {
      throw new Error('usePersistence: use updateState for functional updates');
    }
    stateRef.current = next;
    setState(next);
  }, []);

  return {
    state,
    setState: replaceState,
    stateReady,
    stateLoadError,
    updateState,
    reloadState,
    flushPendingSave,
    stateRef,
    adoptStateVersion,
    beginExternalStateWrite,
    adoptServerEmployee,
    adoptServerTasks,
  };
}
